/*!
 * Parenting Optimize - Knowledge Base
 * File: parenting-knowledge.js
 * Version: 3.0.0
 *
 * Purpose:
 * The structured parenting knowledge layer.
 *
 * This file contains the "brain" that the decision engine reads:
 * - parent natural response logic
 * - child temperament logic
 * - family pattern logic
 * - situation knowledge
 * - recommendation libraries
 * - feedback learning rules
 *
 * Important:
 * This file should not render UI.
 * This file should not manage navigation.
 * This file should not directly touch the DOM.
 */

(function (window) {
  "use strict";

  const PO = (window.PO = window.PO || {});

  // ==========================================================================
  // 01. KNOWLEDGE NAMESPACE
  // ==========================================================================

  PO.Knowledge = {
    version: "3.0.0",

    meta: {
      name: "Parenting Optimize Knowledge Base",
      principle: "Complex parenting logic underneath. Simple guidance on the surface.",
      customerFacingRule: "Never expose methodology names. Translate all logic into practical parent language."
    },

    // ==========================================================================
    // 02. METHODOLOGY MAP
    // These are internal design inspirations only.
    // They should not appear in customer-facing copy unless intentionally used
    // in a separate religious / educational edition.
    // ==========================================================================

    methodologies: {
      naturalResponse: {
        internalName: "Vilna Gaon four-temperament inspired natural response engine",
        publicName: "Natural Parenting Response",
        role: "Predict how a parent tends to react under stress.",
        weight: 15,
        useFor: [
          "parent stress response",
          "anger / intensity tendency",
          "avoidance tendency",
          "over-talking tendency",
          "boundary collapse tendency",
          "rigidity tendency"
        ],
        neverShow: [
          "fire",
          "water",
          "earth",
          "air",
          "middos",
          "Vilna Gaon",
          "Torah source"
        ]
      },

      bigFive: {
        internalName: "Big Five Personality Model",
        publicName: "Parenting Habit Dimensions",
        role: "Borrow useful personality dimensions, translated into parenting behavior.",
        weight: 8,
        useFor: [
          "consistency",
          "flexibility",
          "emotional reactivity",
          "social energy",
          "cooperation style"
        ],
        neverShow: [
          "neuroticism",
          "agreeableness score",
          "conscientiousness percentile"
        ]
      },

      childTemperament: {
        internalName: "Thomas & Chess temperament research",
        publicName: "Child Temperament Profile",
        role: "Understand how the child naturally responds to change, frustration, routines, intensity, and stimulation.",
        weight: 20,
        useFor: [
          "persistence",
          "adaptability",
          "transition difficulty",
          "sensitivity",
          "activity level",
          "emotional intensity",
          "regularity",
          "distractibility"
        ]
      },

      familySystems: {
        internalName: "Bowen family systems theory",
        publicName: "Family Pattern Engine",
        role: "Understand the home pattern rather than blaming one child or one parent.",
        weight: 20,
        useFor: [
          "parent alignment",
          "repeat cycles",
          "rescue loops",
          "split signals",
          "family stress",
          "who carries the emotional load",
          "who enforces",
          "who softens"
        ],
        neverShow: [
          "triangulation",
          "differentiation of self",
          "Bowen"
        ]
      },

      attachment: {
        internalName: "Attachment theory",
        publicName: "Connection Needs",
        role: "Determine when connection must come before correction.",
        weight: 8,
        useFor: [
          "separation sensitivity",
          "correction sensitivity",
          "reassurance needs",
          "repair after conflict"
        ]
      },

      adler: {
        internalName: "Alfred Adler",
        publicName: "Belonging + Contribution Engine",
        role: "Identify how the child can feel important, useful, and meaningfully included.",
        weight: 12,
        useFor: [
          "belonging",
          "importance",
          "family role",
          "contribution",
          "responsibility",
          "purpose"
        ]
      },

      dreikurs: {
        internalName: "Rudolf Dreikurs",
        publicName: "Behavior Purpose Engine",
        role: "Ask what the child may be trying to achieve through the behavior.",
        weight: 8,
        useFor: [
          "attention seeking",
          "power struggle",
          "revenge / hurt response",
          "avoidance of failure",
          "discouragement"
        ]
      },

      rossGreene: {
        internalName: "Ross Greene collaborative problem solving",
        publicName: "Skill Gap Engine",
        role: "Ask what skill may be missing rather than only what consequence is missing.",
        weight: 12,
        useFor: [
          "frustration tolerance",
          "transition skill",
          "problem solving",
          "emotional regulation",
          "language for needs",
          "waiting",
          "flexibility"
        ],
        guidingLine: "Children do well when they can."
      },

      siegel: {
        internalName: "Daniel Siegel / developmental neuroscience",
        publicName: "Brain State Engine",
        role: "Determine whether this is a teaching moment or a calming moment.",
        weight: 6,
        useFor: [
          "meltdown",
          "emotional flooding",
          "repair",
          "name it to tame it",
          "connect before redirect"
        ]
      },

      nelsen: {
        internalName: "Jane Nelsen / Positive Discipline",
        publicName: "Family Climate Engine",
        role: "Balance kindness and firmness, encouragement, gratitude, and respectful correction.",
        weight: 8,
        useFor: [
          "kind and firm",
          "encouragement",
          "logical consequences",
          "family meetings",
          "positive talk",
          "gratitude",
          "repair"
        ]
      },

      haidt: {
        internalName: "Jonathan Haidt / modern childhood environment",
        publicName: "Modern Environment Engine",
        role: "Account for screens, independence, anxiety, social comparison, play, and resilience.",
        weight: 5,
        useFor: [
          "screens",
          "social media",
          "autonomy",
          "outdoor play",
          "resilience",
          "overprotection",
          "friend culture"
        ]
      },

      learningLoop: {
        internalName: "Parenting Optimize adaptive feedback engine",
        publicName: "Learning Loop",
        role: "Remember what works for this child and adjust future recommendations.",
        weight: 20,
        useFor: [
          "feedback",
          "strategy success",
          "strategy failure",
          "child-specific learning",
          "family-specific adaptation",
          "reminders"
        ]
      }
    },

    // ==========================================================================
    // 03. PARENT NATURAL RESPONSE ENGINE
    // Hidden framework. Customer sees only plain-language output.
    // ==========================================================================

    parentResponses: {
      firm: {
        label: "Directive Parent Response",
        strengths: [
          "clear leadership",
          "fast decisions",
          "strong boundaries",
          "protective instinct"
        ],
        underStress: [
          "may increase pressure quickly",
          "may sound sharper than intended",
          "may focus on compliance before connection"
        ],
        bestAdviceStyle: [
          "short scripts",
          "soft opening",
          "firm but low-volume boundary",
          "avoid adding intensity"
        ],
        scriptGuidance: {
          opening: "Start softer than feels natural.",
          boundary: "Keep the boundary firm without increasing volume.",
          repair: "After the moment passes, reconnect briefly."
        }
      },

      explain: {
        label: "Explaining Parent Response",
        strengths: [
          "thoughtful",
          "teaches values",
          "tries to help the child understand",
          "communicates deeply"
        ],
        underStress: [
          "may talk too much",
          "may turn instruction into debate",
          "may accidentally invite negotiation"
        ],
        bestAdviceStyle: [
          "one-sentence instructions",
          "explain later, not during resistance",
          "clear choices",
          "avoid lectures"
        ],
        scriptGuidance: {
          opening: "Use fewer words than feels natural.",
          boundary: "Give one reason, not five.",
          repair: "Teach after calm returns."
        }
      },

      repeat: {
        label: "Repeating Parent Response",
        strengths: [
          "patient at first",
          "tries to avoid conflict",
          "gives chances",
          "does not rush punishment"
        ],
        underStress: [
          "may train the child to wait for the angry voice",
          "may delay follow-through",
          "may become resentful after repeating"
        ],
        bestAdviceStyle: [
          "say it once clearly",
          "one reminder maximum",
          "small follow-through",
          "predictable consequence"
        ],
        scriptGuidance: {
          opening: "Say it clearly the first time.",
          boundary: "Do not restart the instruction after each delay.",
          repair: "Notice cooperation quickly."
        }
      },

      "give-in": {
        label: "Softening Parent Response",
        strengths: [
          "warm",
          "empathetic",
          "emotionally available",
          "cares about the child's feelings"
        ],
        underStress: [
          "may change the rule to reduce distress",
          "may struggle to tolerate crying",
          "may become inconsistent"
        ],
        bestAdviceStyle: [
          "warm boundary",
          "expect protest",
          "do not change the rule immediately",
          "praise recovery"
        ],
        scriptGuidance: {
          opening: "Lead with warmth.",
          boundary: "Hold the limit even while validating feelings.",
          repair: "Let your child know you can handle their big feelings."
        }
      },

      variable: {
        label: "Variable Parent Response",
        strengths: [
          "flexible",
          "can adapt to different situations",
          "often intuitive",
          "not rigid"
        ],
        underStress: [
          "rules may feel unpredictable",
          "child may test which version of the parent shows up",
          "follow-through may depend on energy level"
        ],
        bestAdviceStyle: [
          "simple house rules",
          "repeatable scripts",
          "low-energy routines",
          "pre-decided consequences"
        ],
        scriptGuidance: {
          opening: "Use the same phrase each time.",
          boundary: "Let the rule carry the moment, not your mood.",
          repair: "Consistency matters more than perfection."
        }
      }
    },

    // ==========================================================================
    // 04. CHILD TEMPERAMENT ENGINE
    // ==========================================================================

    childTemperamentDimensions: {
      persistence: {
        low: {
          meaning: "This child may shift away from difficult tasks quickly.",
          usefulStrategies: [
            "short tasks",
            "fast wins",
            "encouragement for effort",
            "visible progress"
          ]
        },
        high: {
          meaning: "This child may hold onto wants, ideas, or resistance for a long time.",
          usefulStrategies: [
            "limited choices",
            "avoid power struggles",
            "give time to shift",
            "stay calm and predictable"
          ]
        }
      },

      adaptability: {
        low: {
          meaning: "This child may struggle when plans change or when one activity ends.",
          usefulStrategies: [
            "warnings before transitions",
            "visual routines",
            "countdowns",
            "first tiny step"
          ]
        },
        high: {
          meaning: "This child may adapt more easily to change.",
          usefulStrategies: [
            "simple direct instructions",
            "quick transitions",
            "let them help choose next step"
          ]
        }
      },

      sensitivity: {
        low: {
          meaning: "This child may not notice subtle emotional cues or small environmental changes.",
          usefulStrategies: [
            "clear words",
            "specific feedback",
            "physical routine cues"
          ]
        },
        high: {
          meaning: "This child may react strongly to tone, criticism, noise, crowds, clothes, or pressure.",
          usefulStrategies: [
            "soft voice",
            "connection before correction",
            "reduce stimulation",
            "private correction"
          ]
        }
      },

      intensity: {
        low: {
          meaning: "This child may show feelings quietly, even when upset.",
          usefulStrategies: [
            "check in gently",
            "watch body language",
            "invite words"
          ]
        },
        high: {
          meaning: "This child may feel and express emotion strongly.",
          usefulStrategies: [
            "calm first",
            "few words",
            "physical space",
            "teach after the storm"
          ]
        }
      },

      regularity: {
        low: {
          meaning: "This child may not naturally fall into predictable rhythms.",
          usefulStrategies: [
            "external structure",
            "visual schedule",
            "same order each day",
            "reduce decision points"
          ]
        },
        high: {
          meaning: "This child may do best when routines stay predictable.",
          usefulStrategies: [
            "protect routines",
            "prepare for exceptions",
            "give advance notice"
          ]
        }
      },

      activity: {
        low: {
          meaning: "This child may prefer quieter or slower-paced routines.",
          usefulStrategies: [
            "gentle transitions",
            "avoid rushing",
            "build in quiet reset time"
          ]
        },
        high: {
          meaning: "This child may need movement and physical outlet.",
          usefulStrategies: [
            "movement before seated tasks",
            "active responsibilities",
            "short bursts",
            "hands-on jobs"
          ]
        }
      }
    },

    childStrengthProfiles: {
      creative: {
        label: "Creative Contributor",
        whatHelps: [
          "choice",
          "imagination",
          "visual tools",
          "creative family roles"
        ],
        contributionIdeas: [
          "Design the family routine chart.",
          "Create a family game for after dinner.",
          "Decorate the table for a family meal.",
          "Plan a rainy-day activity."
        ],
        praiseStyle: "Praise originality and effort, not just the final result."
      },

      technical: {
        label: "Problem-Solving Contributor",
        whatHelps: [
          "practical jobs",
          "tools",
          "systems",
          "real responsibility"
        ],
        contributionIdeas: [
          "Organize chargers and devices.",
          "Check batteries in household items.",
          "Help assemble or fix something safe.",
          "Create a simple checklist for a routine."
        ],
        praiseStyle: "Praise usefulness, persistence, and problem solving."
      },

      sensitive: {
        label: "Sensitive Connector",
        whatHelps: [
          "soft tone",
          "privacy",
          "reassurance",
          "repair after conflict"
        ],
        contributionIdeas: [
          "Make a kind note for someone.",
          "Help prepare something thoughtful.",
          "Be in charge of welcoming guests.",
          "Help a younger sibling with something gentle."
        ],
        praiseStyle: "Praise care, noticing, and emotional effort."
      },

      "strong-willed": {
        label: "Determined Leader",
        whatHelps: [
          "controlled leadership",
          "limited choices",
          "clear boundaries",
          "respectful tone"
        ],
        contributionIdeas: [
          "Lead cleanup for five minutes.",
          "Choose between two family activities.",
          "Be responsible for one part of the routine.",
          "Help decide the order of tasks."
        ],
        praiseStyle: "Praise leadership used responsibly."
      },

      funny: {
        label: "Joy Builder",
        whatHelps: [
          "humor",
          "connection",
          "playful transitions",
          "being noticed positively"
        ],
        contributionIdeas: [
          "Tell a joke at dinner.",
          "Create a funny family award.",
          "Make a boring task into a race.",
          "Lead a short family game."
        ],
        praiseStyle: "Praise using humor to lift the family, not distract from responsibility."
      },

      helpful: {
        label: "Helper",
        whatHelps: [
          "being trusted",
          "meaningful jobs",
          "gratitude",
          "specific appreciation"
        ],
        contributionIdeas: [
          "Prepare snacks.",
          "Help a younger sibling.",
          "Set the table.",
          "Be the family kindness captain."
        ],
        praiseStyle: "Praise reliability and kindness."
      },

      organized: {
        label: "Organizer",
        whatHelps: [
          "order",
          "predictability",
          "checklists",
          "ownership"
        ],
        contributionIdeas: [
          "Set up the morning checklist.",
          "Organize school supplies.",
          "Plan the order of bedtime steps.",
          "Create a family calendar reminder."
        ],
        praiseStyle: "Praise planning and follow-through."
      },

      energetic: {
        label: "Energy Mover",
        whatHelps: [
          "movement",
          "short tasks",
          "physical jobs",
          "active transitions"
        ],
        contributionIdeas: [
          "Carry laundry.",
          "Run a quick errand inside the house.",
          "Lead a two-minute movement break.",
          "Help bring bags in from the car."
        ],
        praiseStyle: "Praise channeling energy into helpful action."
      }
    },

    // ==========================================================================
    // 05. FAMILY PATTERN ENGINE
    // ==========================================================================

    familyStructures: {
      single: {
        label: "Single-Parent Load",
        likelyChallenges: [
          "less backup",
          "higher emotional load",
          "harder to stay consistent when tired"
        ],
        planBias: [
          "simple routines",
          "low-energy scripts",
          "fewer moving parts",
          "make follow-through easy"
        ],
        language: "The plan should be simple enough to use without another adult stepping in."
      },

      "two-together": {
        label: "Two-Parent Alignment",
        likelyChallenges: [
          "different parenting instincts",
          "one parent may enforce while the other softens",
          "child may learn who to ask"
        ],
        planBias: [
          "agree on the rule first",
          "same wording",
          "avoid correcting each other in front of the child",
          "repair privately"
        ],
        language: "The plan should reduce mixed signals and make the rule clear from both adults."
      },

      shared: {
        label: "Shared-Custody Consistency",
        likelyChallenges: [
          "different homes",
          "different rules",
          "transition stress",
          "child may compare households"
        ],
        planBias: [
          "control what happens in this home",
          "avoid criticizing the other home",
          "predictable house rules",
          "transition rituals"
        ],
        language: "The plan should focus on predictable routines in your home, even if other settings are different."
      },

      other: {
        label: "Complex Family Setup",
        likelyChallenges: [
          "multiple adults",
          "unclear authority",
          "inconsistent expectations",
          "emotional triangles"
        ],
        planBias: [
          "clarify who is responsible",
          "reduce public disagreement",
          "use simple rules",
          "avoid multiple adults negotiating at once"
        ],
        language: "The plan should reduce confusion by clarifying the rule and the responsible adult."
      }
    },

    familyPatterns: {
      escalationLoop: {
        label: "Escalation Loop",
        signs: [
          "parent repeats",
          "child delays",
          "parent gets louder",
          "child complies only after intensity rises"
        ],
        breakLoop: [
          "one calm instruction",
          "one reminder",
          "small follow-through",
          "repair later"
        ]
      },

      negotiationLoop: {
        label: "Negotiation Loop",
        signs: [
          "every instruction becomes discussion",
          "child asks why repeatedly",
          "parent over-explains",
          "task gets delayed"
        ],
        breakLoop: [
          "one reason",
          "two choices",
          "stop debating",
          "teach later"
        ]
      },

      rescueLoop: {
        label: "Rescue Loop",
        signs: [
          "child protests",
          "parent softens",
          "rule changes",
          "child learns distress changes boundaries"
        ],
        breakLoop: [
          "validate feeling",
          "hold rule",
          "expect protest",
          "praise recovery"
        ]
      },

      splitSignal: {
        label: "Split-Signal Pattern",
        signs: [
          "adults give different answers",
          "child asks the softer adult",
          "one parent undermines the other",
          "rules depend on who is present"
        ],
        breakLoop: [
          "align privately",
          "use same sentence",
          "do not debate in front of child",
          "repair parent unity"
        ]
      },

      exhaustionLoop: {
        label: "Exhaustion Loop",
        signs: [
          "parent knows what to do but is too tired",
          "rules collapse at the end of the day",
          "child pushes harder when parent has low energy"
        ],
        breakLoop: [
          "simplify routine",
          "lower the number of decisions",
          "use scripts",
          "choose one battle"
        ]
      }
    },

    // ==========================================================================
    // 06. BEHAVIOR PURPOSE ENGINE
    // ==========================================================================

    behaviorPurposes: {
      attention: {
        label: "Connection Seeking",
        childBelief: "I matter when I get a reaction.",
        parentMove: [
          "give attention before the behavior escalates",
          "avoid big emotional reactions",
          "notice small positive behaviors quickly"
        ],
        phrase: "I see you. I will help you after this next step."
      },

      power: {
        label: "Control Seeking",
        childBelief: "I belong when I am in control.",
        parentMove: [
          "offer limited choices",
          "avoid direct power struggles",
          "let the child have control over small acceptable details"
        ],
        phrase: "You can choose this way or that way. The rule stays the same."
      },

      revenge: {
        label: "Hurt Response",
        childBelief: "If I feel hurt, I will hurt back.",
        parentMove: [
          "do not retaliate",
          "name the hurt later",
          "repair relationship after calm"
        ],
        phrase: "I will not let us hurt each other. We will fix this when we are calmer."
      },

      avoidance: {
        label: "Avoiding Failure",
        childBelief: "If I do not try, I cannot fail.",
        parentMove: [
          "make the task smaller",
          "praise effort",
          "remove shame",
          "start together"
        ],
        phrase: "You do not have to finish all of it now. Start with one small part."
      }
    },

    // ==========================================================================
    // 07. SKILL GAP ENGINE
    // ==========================================================================

    skillGaps: {
      transition: {
        label: "Transition Skill",
        signs: [
          "struggles when one activity ends",
          "needs many reminders to shift",
          "gets upset when plans change"
        ],
        buildWith: [
          "warnings",
          "visual routine",
          "first small step",
          "practice during calm times"
        ]
      },

      frustrationTolerance: {
        label: "Frustration Tolerance",
        signs: [
          "melts down when something is hard",
          "gives up quickly",
          "becomes angry when corrected"
        ],
        buildWith: [
          "short hard tasks",
          "encourage effort",
          "model calm mistake recovery",
          "avoid rescuing too fast"
        ]
      },

      flexibility: {
        label: "Flexibility",
        signs: [
          "needs things done one way",
          "argues when plans change",
          "gets stuck on fairness"
        ],
        buildWith: [
          "preview changes",
          "practice Plan B",
          "offer limited choices",
          "praise flexible moments"
        ]
      },

      communication: {
        label: "Needs Communication",
        signs: [
          "acts out instead of explaining",
          "says rude things when upset",
          "cannot ask for help calmly"
        ],
        buildWith: [
          "short scripts",
          "feelings words",
          "practice requests",
          "repair conversations"
        ]
      },

      impulseControl: {
        label: "Impulse Control",
        signs: [
          "grabs",
          "interrupts",
          "hits before thinking",
          "runs ahead"
        ],
        buildWith: [
          "pause practice",
          "physical structure",
          "clear boundaries",
          "short immediate feedback"
        ]
      }
    },

    // ==========================================================================
    // 08. BRAIN STATE ENGINE
    // ==========================================================================

    brainStates: {
      calm: {
        label: "Teaching Time",
        parentMove: [
          "teach",
          "practice",
          "ask reflective questions",
          "make agreements"
        ],
        avoid: [
          "over-talking",
          "lecturing"
        ]
      },

      rising: {
        label: "Reduce Intensity",
        parentMove: [
          "lower voice",
          "reduce words",
          "give space",
          "use simple choices"
        ],
        avoid: [
          "debating",
          "threatening"
        ]
      },

      flooded: {
        label: "Calm First",
        parentMove: [
          "safety first",
          "few words",
          "calm body",
          "teach later"
        ],
        avoid: [
          "logic",
          "consequences added in anger",
          "long explanations"
        ]
      },

      recovery: {
        label: "Repair Window",
        parentMove: [
          "reconnect",
          "name what happened simply",
          "practice one better response"
        ],
        avoid: [
          "restarting the fight",
          "shaming"
        ]
      }
    },

    // ==========================================================================
    // 09. FAMILY CLIMATE ENGINE
    // ==========================================================================

    familyClimate: {
      encouragement: {
        principle: "Children usually do better when they feel noticed for effort, usefulness, and progress.",
        practicalMoves: [
          "notice one good thing out loud each day",
          "speak positively about the child to another adult where the child may hear",
          "thank the child for a specific contribution",
          "write down one thing that went well"
        ]
      },

      kindAndFirm: {
        principle: "The goal is not harshness or permissiveness. The goal is warmth with a clear limit.",
        script: "I understand you are upset. The answer is still no."
      },

      repair: {
        principle: "Repair teaches children that conflict does not break the relationship.",
        script: "That was a hard moment. I love you. Next time we will try it differently."
      },

      gratitude: {
        principle: "A home that notices good creates more emotional safety.",
        practicalMoves: [
          "family gratitude at dinner",
          "one positive note per child per week",
          "thank children for unseen effort",
          "notice progress, not perfection"
        ]
      }
    },

    // ==========================================================================
    // 10. MODERN ENVIRONMENT ENGINE
    // ==========================================================================

    modernEnvironment: {
      screens: {
        risks: [
          "hard stopping points",
          "dopamine contrast with boring tasks",
          "negotiation after time is up",
          "sleep disruption"
        ],
        moves: [
          "set rule before screen starts",
          "use visual timer",
          "no negotiating at cutoff",
          "transition to a physical activity"
        ]
      },

      independence: {
        risks: [
          "too little responsibility",
          "too few real-world jobs",
          "low confidence",
          "excessive adult rescue"
        ],
        moves: [
          "give real responsibilities",
          "allow safe struggle",
          "avoid doing everything for the child",
          "praise capability"
        ]
      },

      play: {
        risks: [
          "not enough movement",
          "not enough peer negotiation",
          "too much adult-managed time"
        ],
        moves: [
          "build in free play",
          "allow age-appropriate independence",
          "use physical movement before seated tasks"
        ]
      }
    },

    // ==========================================================================
    // 11. SITUATION LIBRARY
    // V1 has core situations. Later this grows to hundreds.
    // ==========================================================================

    situations: {
      bedtime: {
        label: "Bedtime",
        category: "routine",
        likelyDrivers: [
          "transition difficulty",
          "separation need",
          "overtired child",
          "too many negotiations",
          "screen cutoff friction"
        ],
        primarySkills: [
          "transition",
          "self-regulation",
          "routine following"
        ],
        defaultPlan: {
          steps: [
            "Start the routine before the child is fully exhausted.",
            "Use the same short sequence each night.",
            "Give one calm choice inside the routine."
          ],
          avoid: [
            "Do not reopen negotiations after the routine begins.",
            "Do not add new consequences while angry.",
            "Do not turn bedtime into a long discussion."
          ]
        },
        parentScript: {
          soft: "I know you want more time. It is bedtime now. You can choose pajamas first or teeth first.",
          firm: "Bedtime has started. Choose pajamas or teeth. I will help you begin.",
          repair: "That was hard. Tomorrow we will start a little earlier."
        }
      },

      "not-listening": {
        label: "Not Listening",
        category: "cooperation",
        likelyDrivers: [
          "repeat-resist loop",
          "unclear instruction",
          "too many words",
          "child waiting for intensity"
        ],
        primarySkills: [
          "following directions",
          "attention",
          "transition"
        ],
        defaultPlan: {
          steps: [
            "Get close before giving the instruction.",
            "Say the instruction once clearly.",
            "Follow through with one small action instead of repeating."
          ],
          avoid: [
            "Do not call instructions from across the house.",
            "Do not repeat seven times.",
            "Do not wait until you are angry to act."
          ]
        },
        parentScript: {
          soft: "I need your eyes for one second. It is time to put your shoes on.",
          firm: "Shoes now. You can do it yourself or I can help you start.",
          repair: "You listened faster that time. That helped the house move calmly."
        }
      },

      screens: {
        label: "Screen Time",
        category: "technology",
        likelyDrivers: [
          "hard stopping point",
          "high reward activity",
          "rule unclear before starting",
          "parent negotiates at cutoff"
        ],
        primarySkills: [
          "stopping",
          "waiting",
          "frustration tolerance"
        ],
        defaultPlan: {
          steps: [
            "State the screen rule before the screen begins.",
            "Use a visible timer or countdown.",
            "When time ends, move to the next activity without renegotiating."
          ],
          avoid: [
            "Do not create the rule after the child is already watching.",
            "Do not debate when time is up.",
            "Do not extend time because of protest."
          ]
        },
        parentScript: {
          soft: "You can watch for twenty minutes. When the timer rings, the screen goes away.",
          firm: "The timer rang. Screen is done. You can put it away or I can help.",
          repair: "Stopping is hard. You did it, and that matters."
        }
      },

      homework: {
        label: "Homework",
        category: "school",
        likelyDrivers: [
          "avoidance of failure",
          "overwhelm",
          "skill gap",
          "too large a task",
          "parent-child power struggle"
        ],
        primarySkills: [
          "starting",
          "planning",
          "frustration tolerance"
        ],
        defaultPlan: {
          steps: [
            "Shrink the task to the first tiny step.",
            "Stay nearby for the first two minutes.",
            "Praise effort before correcting quality."
          ],
          avoid: [
            "Do not begin with the entire assignment.",
            "Do not shame the child for struggling.",
            "Do not turn homework into a battle about character."
          ]
        },
        parentScript: {
          soft: "We are not doing all of it right now. Start with the first problem.",
          firm: "The first step is opening the book. I will sit here for two minutes.",
          repair: "Starting was hard, but you started. That is progress."
        }
      },

      morning: {
        label: "Morning Routine",
        category: "routine",
        likelyDrivers: [
          "transition difficulty",
          "too many decisions",
          "parent stress",
          "unclear order"
        ],
        primarySkills: [
          "routine",
          "sequencing",
          "time awareness"
        ],
        defaultPlan: {
          steps: [
            "Use the same order every morning.",
            "Reduce choices before school.",
            "Give one small job at a time."
          ],
          avoid: [
            "Do not give five instructions at once.",
            "Do not start the day with criticism.",
            "Do not change the routine every morning."
          ]
        },
        parentScript: {
          soft: "First clothes, then breakfast. I will check back in two minutes.",
          firm: "Clothes first. Breakfast after. Start now.",
          repair: "This morning moved better when we used the same order."
        }
      },

      siblings: {
        label: "Sibling Conflict",
        category: "relationship",
        likelyDrivers: [
          "competition for attention",
          "unfairness feeling",
          "poor repair skill",
          "one child feels unseen"
        ],
        primarySkills: [
          "repair",
          "sharing",
          "problem solving",
          "emotional language"
        ],
        defaultPlan: {
          steps: [
            "Separate briefly if emotions are high.",
            "Ask what each child wanted or needed.",
            "Give each child one repair action."
          ],
          avoid: [
            "Do not decide the whole case while everyone is yelling.",
            "Do not label one child as always the problem.",
            "Do not force fake apologies too early."
          ]
        },
        parentScript: {
          soft: "Pause. I am going to hear both of you when voices are calmer.",
          firm: "Hands are not for hurting. Separate now. We will fix it after.",
          repair: "Each person needs one step to repair what happened."
        }
      },

      arguing: {
        label: "Arguing",
        category: "communication",
        likelyDrivers: [
          "negotiation loop",
          "need for control",
          "parent over-explains",
          "child delays through debate"
        ],
        primarySkills: [
          "respectful disagreement",
          "accepting limits",
          "flexibility"
        ],
        defaultPlan: {
          steps: [
            "Give one short reason.",
            "Offer two acceptable choices.",
            "Stop answering repeated objections."
          ],
          avoid: [
            "Do not argue as if you are trying to win a court case.",
            "Do not keep adding new explanations.",
            "Do not reward arguing with endless attention."
          ]
        },
        parentScript: {
          soft: "I hear that you disagree. The choice is A or B.",
          firm: "I answered. I am not debating this now.",
          repair: "You may disagree respectfully. We can talk more later."
        }
      },

      meltdown: {
        label: "Meltdown",
        category: "emotion",
        likelyDrivers: [
          "emotional flooding",
          "hunger",
          "tiredness",
          "overstimulation",
          "skill gap"
        ],
        primarySkills: [
          "regulation",
          "recovery",
          "language for feelings"
        ],
        defaultPlan: {
          steps: [
            "Focus on safety and lowering intensity.",
            "Use very few words.",
            "Teach or discuss only after the child is calmer."
          ],
          avoid: [
            "Do not lecture during the peak.",
            "Do not demand insight while the child is flooded.",
            "Do not add big consequences while you are upset."
          ]
        },
        parentScript: {
          soft: "You are having a hard time. I am here. We are going to slow down.",
          firm: "I will not let you hurt anyone. I am moving this away.",
          repair: "That was a big feeling. Next time we will practice what to do first."
        }
      }
    },

    // ==========================================================================
    // 12. RECOMMENDATION MODIFIERS
    // These modify situation advice based on parent / child / family profile.
    // ==========================================================================

    modifiers: {
      parent: {
        directive: {
          add: [
            "Use a softer opening before the boundary.",
            "Keep your voice lower than your instinct wants."
          ],
          avoid: [
            "Do not make the moment bigger with extra intensity."
          ]
        },

        explainer: {
          add: [
            "Use fewer words than feels natural.",
            "Save the lesson for later."
          ],
          avoid: [
            "Do not answer every objection."
          ]
        },

        repeater: {
          add: [
            "Decide your follow-through before giving the instruction.",
            "Use one reminder maximum."
          ],
          avoid: [
            "Do not repeat until you become angry."
          ]
        },

        rescuer: {
          add: [
            "Validate the feeling without changing the rule.",
            "Expect protest and remain warm."
          ],
          avoid: [
            "Do not remove the boundary just because the child is upset."
          ]
        },

        inconsistent: {
          add: [
            "Use the same phrase each time.",
            "Choose one small rule you can actually enforce today."
          ],
          avoid: [
            "Do not create rules in the middle of the conflict."
          ]
        }
      },

      child: {
        highPersistence: {
          add: [
            "Offer limited choices, not open-ended negotiation.",
            "Give the child time to shift without chasing the argument."
          ],
          avoid: [
            "Do not enter a power contest."
          ]
        },

        highSensitivity: {
          add: [
            "Correct privately when possible.",
            "Use a softer tone and repair afterward."
          ],
          avoid: [
            "Do not shame or criticize in front of others."
          ]
        },

        transitionDifficulty: {
          add: [
            "Give a warning before the change.",
            "Make the first step very small."
          ],
          avoid: [
            "Do not surprise the child with abrupt transitions."
          ]
        },

        highEnergy: {
          add: [
            "Use movement before seated tasks.",
            "Give a physical job when possible."
          ],
          avoid: [
            "Do not expect long stillness before regulation."
          ]
        }
      },

      family: {
        singleParentLoad: {
          add: [
            "Keep the plan simple enough to use without backup.",
            "Choose the smallest consequence that you can calmly enforce."
          ],
          avoid: [
            "Do not choose a strategy that requires more energy than you have tonight."
          ]
        },

        twoParentAlignment: {
          add: [
            "Align with the other parent privately when possible.",
            "Use the same rule wording."
          ],
          avoid: [
            "Do not correct the other adult in front of the child."
          ]
        },

        sharedCustody: {
          add: [
            "Focus on predictable rules in your home.",
            "Avoid comparing homes during the conflict."
          ],
          avoid: [
            "Do not criticize the other home in front of the child."
          ]
        }
      }
    },

    // ==========================================================================
    // 13. FEEDBACK LEARNING RULES
    // ==========================================================================

    feedbackRules: {
      worked: {
        meaning: "Strategy appears helpful.",
        futureBias: [
          "repeat similar approach",
          "increase confidence",
          "offer same structure in related situations"
        ],
        parentMessage: "Good. We'll remember that this kind of approach may work for this child."
      },

      somewhat: {
        meaning: "Strategy partly helped.",
        futureBias: [
          "keep core approach",
          "simplify steps",
          "adjust intensity"
        ],
        parentMessage: "Helpful to know. Next time we may simplify the plan."
      },

      "did-not-work": {
        meaning: "Strategy did not help.",
        futureBias: [
          "try alternate approach",
          "check wrong driver",
          "ask one more context question"
        ],
        parentMessage: "That matters. We'll adjust instead of repeating the same plan."
      },

      worse: {
        meaning: "Strategy escalated the situation.",
        futureBias: [
          "avoid similar approach",
          "prioritize calming",
          "reduce direct pressure",
          "ask if child was flooded"
        ],
        parentMessage: "Important feedback. Next time we should slow the moment down first."
      },

      "not-tried": {
        meaning: "No outcome data.",
        futureBias: [
          "remind gently",
          "make plan easier to try",
          "reduce number of steps"
        ],
        parentMessage: "No problem. We'll keep the next plan simple."
      }
    },



    // ==========================================================================
    // 14A. V3 SEVEN-TABLE REASONING MODEL
    // This is additive. It does not replace the older methodology, temperament,
    // family, situation, modifier, feedback, or output-style libraries above.
    // ========================================================================== 

    v3: {
      version: "3.0.0",
      principle: "Observe behavior, infer possible constructs, ask targeted questions, then build a practical plan.",

      // ------------------------------------------------------------------------
      // TABLE 1 — SCENARIO MAP
      // ------------------------------------------------------------------------

      scenarios: {
        S01: {
          key: "bedtime-struggles",
          label: "Bedtime Struggles",
          primaryGoal: "Understand why bedtime becomes difficult and recommend personalized evening strategies.",
          situationKey: "bedtime"
        },
        S02: {
          key: "morning-routine",
          label: "Morning Routine",
          primaryGoal: "Improve transitions, independence and reduce morning conflict.",
          situationKey: "morning"
        },
        S03: {
          key: "homework-refusal",
          label: "Homework Refusal",
          primaryGoal: "Identify whether resistance is caused by executive function, confidence, anxiety or motivation.",
          situationKey: "homework"
        },
        S04: {
          key: "doesnt-listen",
          label: "Doesn't Listen",
          primaryGoal: "Determine whether non-compliance is driven by attention, transitions, control or understanding.",
          situationKey: "not-listening"
        },
        S05: {
          key: "negotiates-everything",
          label: "Negotiates Everything",
          primaryGoal: "Understand why the child challenges limits and identify healthy autonomy strategies.",
          situationKey: "arguing"
        },
        S06: {
          key: "tantrums-meltdowns",
          label: "Tantrums / Meltdowns",
          primaryGoal: "Differentiate emotional overload from frustration, sensory issues or developmental regulation.",
          situationKey: "meltdown"
        },
        S07: {
          key: "emotional-outbursts",
          label: "Emotional Outbursts",
          primaryGoal: "Help parents respond to intense emotions while identifying underlying contributors.",
          situationKey: "meltdown"
        },
        S08: {
          key: "screen-time-battles",
          label: "Screen Time Battles",
          primaryGoal: "Improve transitions away from preferred activities and reduce conflict.",
          situationKey: "screens"
        },
        S09: {
          key: "sibling-conflict",
          label: "Sibling Conflict",
          primaryGoal: "Understand fairness, attention and relationship dynamics.",
          situationKey: "siblings"
        },
        S10: {
          key: "lying",
          label: "Lying",
          primaryGoal: "Explore fear, shame, avoidance, impulsivity or trust issues.",
          situationKey: "lying"
        },
        S11: {
          key: "anxiety-worry",
          label: "Anxiety / Worry",
          primaryGoal: "Understand fears, uncertainty and reassurance-seeking behaviors.",
          situationKey: "anxiety"
        },
        S12: {
          key: "arguing",
          label: "Arguing",
          primaryGoal: "Determine whether arguments are driven by autonomy, fairness, persistence or emotional regulation.",
          situationKey: "arguing"
        }
      },

      // ------------------------------------------------------------------------
      // TABLE 2 — OBSERVATION LIBRARY
      // These are parent-visible behaviors. They are not diagnoses.
      // ------------------------------------------------------------------------

      observations: {
        communicationCompliance: {
          label: "Communication & Compliance",
          items: [
            "Doesn't listen the first time",
            "Needs repeated reminders",
            "Negotiates after hearing No",
            "Argues every instruction",
            "Constantly asks Why",
            "Ignores instructions",
            "Pretends not to hear",
            "Says Okay but doesn't act",
            "Talks back",
            "Interrupts constantly",
            "Blames others",
            "Makes excuses"
          ]
        },
        transitions: {
          label: "Transitions",
          items: [
            "Refuses bedtime",
            "Leaves bedroom repeatedly",
            "Cannot stop playing",
            "Meltdown during transitions",
            "Refuses morning routine",
            "Upset when plans change",
            "Refuses leaving friends",
            "Needs multiple warnings",
            "Difficulty ending screen time",
            "Refuses leaving the house"
          ]
        },
        emotionalRegulation: {
          label: "Emotional Regulation",
          items: [
            "Cries easily",
            "Screams",
            "Throws objects",
            "Slams doors",
            "Hits",
            "Kicks",
            "Runs away",
            "Shuts down",
            "Withdraws",
            "Freezes",
            "Emotional overreaction",
            "Slow recovery after becoming upset"
          ]
        },
        anxiety: {
          label: "Anxiety",
          items: [
            "Needs reassurance",
            "Worries excessively",
            "Afraid of sleeping alone",
            "Afraid of making mistakes",
            "Avoids new situations",
            "Clings to parents",
            "Stomach aches before school",
            "Avoids trying",
            "Panic before events"
          ]
        },
        learning: {
          label: "Learning",
          items: [
            "Refuses homework",
            "Gives up quickly",
            "Says I can't",
            "Avoids reading",
            "Doesn't finish work",
            "Easily distracted",
            "Rushes work",
            "Perfectionism",
            "Erases repeatedly"
          ]
        },
        social: {
          label: "Social",
          items: [
            "Doesn't share",
            "Wants to win everything",
            "Bosses siblings",
            "Difficulty taking turns",
            "Bullies others",
            "Gets bullied",
            "Avoids children",
            "Difficulty making friends",
            "Wants all the attention",
            "Jealous of siblings"
          ]
        },
        independence: {
          label: "Independence",
          items: [
            "Won't dress independently",
            "Won't clean room",
            "Avoids chores",
            "Expects parents to do everything",
            "Refuses responsibility",
            "Won't make decisions"
          ]
        },
        honesty: {
          label: "Honesty",
          items: [
            "Lies",
            "Hides mistakes",
            "Blames others",
            "Steals",
            "Hides belongings"
          ]
        },
        sensory: {
          label: "Sensory",
          items: [
            "Covers ears",
            "Refuses clothing",
            "Picky eating",
            "Noise sensitivity",
            "Touch sensitivity",
            "Easily overwhelmed"
          ]
        },
        executiveFunction: {
          label: "Executive Function",
          items: [
            "Forgets instructions",
            "Loses belongings",
            "Starts but doesn't finish",
            "Poor organization",
            "Time blindness",
            "Easily distracted"
          ]
        },
        confidence: {
          label: "Confidence",
          items: [
            "Afraid to try",
            "Fear of failure",
            "Needs reassurance",
            "Won't answer in class",
            "Avoids challenges"
          ]
        }
      },

      // ------------------------------------------------------------------------
      // TABLE 3 — UNDERLYING CONSTRUCTS
      // Internal inference layer, translated into parent-friendly language.
      // ------------------------------------------------------------------------

      constructs: {
        C01: {
          label: "Need for Control",
          parentMeaning: "Wants influence over decisions"
        },
        C02: {
          label: "Transition Difficulty",
          parentMeaning: "Struggles switching between activities"
        },
        C03: {
          label: "Executive Function Difficulty",
          parentMeaning: "Difficulty planning, remembering and completing tasks"
        },
        C04: {
          label: "Attention Regulation",
          parentMeaning: "Easily distracted or overly focused"
        },
        C05: {
          label: "Emotional Sensitivity",
          parentMeaning: "Feels emotions very deeply"
        },
        C06: {
          label: "Emotional Dysregulation",
          parentMeaning: "Has difficulty calming down once upset"
        },
        C07: {
          label: "Anxiety / Worry",
          parentMeaning: "Fear or uncertainty drives behavior"
        },
        C08: {
          label: "Fear of Failure",
          parentMeaning: "Avoids mistakes or difficult tasks"
        },
        C09: {
          label: "Low Confidence",
          parentMeaning: "Doubts own abilities"
        },
        C10: {
          label: "Fairness Sensitivity",
          parentMeaning: "Strong reaction to perceived unfairness"
        },
        C11: {
          label: "High Persistence",
          parentMeaning: "Keeps pushing long after others stop"
        },
        C12: {
          label: "Sensory Sensitivity",
          parentMeaning: "Strong reactions to sounds, clothing, food or touch"
        },
        C13: {
          label: "Connection Seeking",
          parentMeaning: "Needs reassurance or closeness"
        },
        C14: {
          label: "Learned Negotiation",
          parentMeaning: "Has learned persistence changes outcomes"
        },
        C15: {
          label: "Inconsistent Boundaries",
          parentMeaning: "Different adult responses create confusion"
        }
      },

      // ------------------------------------------------------------------------
      // TABLE 4 — OBSERVATION TO CONSTRUCT MAPPING
      // Ordered construct IDs imply strongest likely driver first.
      // ------------------------------------------------------------------------

      observationConstructMap: {
        "Doesn't listen first time": ["C03", "C04", "C02", "C01"],
        "Doesn't listen the first time": ["C03", "C04", "C02", "C01"],
        "Needs repeated reminders": ["C03", "C04", "C15"],
        "Negotiates after No": ["C01", "C10", "C11", "C14"],
        "Negotiates after hearing No": ["C01", "C10", "C11", "C14"],
        "Argues every instruction": ["C01", "C10", "C11", "C15"],
        "Constantly asks Why": ["C10", "C01", "C07"],
        "Says Okay but doesn't act": ["C03", "C04", "C14"],
        "Refuses bedtime": ["C02", "C07", "C12", "C13"],
        "Refuses morning routine": ["C02", "C03", "C07"],
        "Can't stop playing": ["C02", "C11", "C04"],
        "Cannot stop playing": ["C02", "C11", "C04"],
        "Resists ending screen time": ["C02", "C11", "C01"],
        "Difficulty ending screen time": ["C02", "C11", "C01"],
        "Cries easily": ["C05", "C06", "C07"],
        "Throws objects": ["C06", "C11"],
        "Shuts down": ["C07", "C05", "C08"],
        "Refuses homework": ["C08", "C09", "C03"],
        "Gives up quickly": ["C08", "C09"],
        "Hides homework": ["C08", "C07"],
        "Lies": ["C07", "C08", "C14"],
        "Blames others": ["C08", "C10", "C15"],
        "Sibling conflict": ["C10", "C01", "C13"],
        "Needs reassurance": ["C07", "C13"],
        "Refuses clothing": ["C12"],
        "Loses belongings": ["C03", "C04"],
        "Time blindness": ["C03"],
        "Avoids challenges": ["C08", "C09"],
        "Wants all the attention": ["C13"],
        "Worries excessively": ["C07", "C13"],
        "Afraid of sleeping alone": ["C07", "C13"],
        "Afraid of making mistakes": ["C08", "C07", "C09"],
        "Avoids new situations": ["C07", "C08"],
        "Clings to parents": ["C13", "C07"],
        "Stomach aches before school": ["C07"],
        "Avoids trying": ["C08", "C09"],
        "Panic before events": ["C07", "C06"],
        "Says I can't": ["C09", "C08"],
        "Doesn't finish work": ["C03", "C04", "C08"],
        "Easily distracted": ["C04", "C03"],
        "Rushes work": ["C04", "C08"],
        "Perfectionism": ["C08", "C07"],
        "Erases repeatedly": ["C08", "C07"],
        "Bosses siblings": ["C01", "C10"],
        "Difficulty taking turns": ["C01", "C11", "C04"],
        "Jealous of siblings": ["C13", "C10"],
        "Won't dress independently": ["C03", "C01", "C12"],
        "Won't clean room": ["C03", "C01"],
        "Avoids chores": ["C01", "C03", "C14"],
        "Expects parents to do everything": ["C13", "C14", "C03"],
        "Refuses responsibility": ["C01", "C08", "C14"],
        "Won't make decisions": ["C08", "C09", "C07"],
        "Hides mistakes": ["C08", "C07"],
        "Covers ears": ["C12"],
        "Picky eating": ["C12", "C01"],
        "Noise sensitivity": ["C12"],
        "Touch sensitivity": ["C12"],
        "Easily overwhelmed": ["C12", "C06", "C05"],
        "Forgets instructions": ["C03", "C04"],
        "Starts but doesn't finish": ["C03", "C04"],
        "Poor organization": ["C03"],
        "Afraid to try": ["C08", "C09"],
        "Fear of failure": ["C08", "C09"],
        "Won't answer in class": ["C09", "C07", "C08"]
      },

      // ------------------------------------------------------------------------
      // TABLE 5 — INTERVENTION LIBRARY
      // Construct-level guidance used by plan generation.
      // ------------------------------------------------------------------------

      interventions: {
        C01: {
          construct: "Need for Control",
          usuallyHelps: ["Choices", "collaboration", "responsibility"],
          usuallyBackfires: ["Power struggles", "commands"]
        },
        C02: {
          construct: "Transition Difficulty",
          usuallyHelps: ["Warnings", "countdowns", "routines"],
          usuallyBackfires: ["Sudden changes"]
        },
        C03: {
          construct: "Executive Function",
          usuallyHelps: ["Visual schedules", "one-step instructions"],
          usuallyBackfires: ["Long verbal instructions"]
        },
        C04: {
          construct: "Attention Regulation",
          usuallyHelps: ["Eye contact", "touch cue", "remove distractions"],
          usuallyBackfires: ["Shouting across the house"]
        },
        C05: {
          construct: "Emotional Sensitivity",
          usuallyHelps: ["Validation", "calm tone"],
          usuallyBackfires: ["Criticism", "dismissal"]
        },
        C06: {
          construct: "Emotional Dysregulation",
          usuallyHelps: ["Co-regulation", "calming routines"],
          usuallyBackfires: ["Lectures during meltdown"]
        },
        C07: {
          construct: "Anxiety",
          usuallyHelps: ["Predictability", "gradual exposure"],
          usuallyBackfires: ["Just stop worrying"]
        },
        C08: {
          construct: "Fear of Failure",
          usuallyHelps: ["Praise effort", "reduce task size"],
          usuallyBackfires: ["Pressure", "comparison"]
        },
        C09: {
          construct: "Low Confidence",
          usuallyHelps: ["Small wins", "encouragement"],
          usuallyBackfires: ["Public criticism"]
        },
        C10: {
          construct: "Fairness Sensitivity",
          usuallyHelps: ["Explain reasoning", "consistency"],
          usuallyBackfires: ["Because I said so"]
        },
        C11: {
          construct: "High Persistence",
          usuallyHelps: ["Boundaries", "closure rituals"],
          usuallyBackfires: ["Endless debates"]
        },
        C12: {
          construct: "Sensory Sensitivity",
          usuallyHelps: ["Environmental adjustments"],
          usuallyBackfires: ["Forcing discomfort"]
        },
        C13: {
          construct: "Connection Seeking",
          usuallyHelps: ["Connection before correction"],
          usuallyBackfires: ["Emotional withdrawal"]
        },
        C14: {
          construct: "Learned Negotiation",
          usuallyHelps: ["Consistent boundaries"],
          usuallyBackfires: ["Giving in after arguing"]
        },
        C15: {
          construct: "Inconsistent Boundaries",
          usuallyHelps: ["Parent consistency"],
          usuallyBackfires: ["Mixed messages"]
        }
      },

      // ------------------------------------------------------------------------
      // TABLE 6 — ADAPTIVE QUESTION LIBRARY
      // Used after initial observation scoring to increase confidence.
      // ------------------------------------------------------------------------

      adaptiveQuestions: [
        {
          id: "AQ01",
          question: "What happens when plans suddenly change?",
          measures: ["C02", "C07"],
          labels: ["Transition Difficulty", "Anxiety"]
        },
        {
          id: "AQ02",
          question: "When your child hears No, do they become upset or try to persuade you?",
          measures: ["C01", "C10", "C11"],
          labels: ["Control", "Fairness", "Persistence"]
        },
        {
          id: "AQ03",
          question: "Does giving choices make things easier?",
          measures: ["C01"],
          labels: ["Need for Control"]
        },
        {
          id: "AQ04",
          question: "Does this happen with both parents?",
          measures: ["C15"],
          labels: ["Family Consistency"]
        },
        {
          id: "AQ05",
          question: "Does your child calm faster with space or with closeness?",
          measures: ["C06", "C13"],
          labels: ["Emotional Regulation", "Connection"]
        },
        {
          id: "AQ06",
          question: "How do they react when they lose a game?",
          measures: ["C06", "C10"],
          labels: ["Emotional Regulation", "Fairness"]
        },
        {
          id: "AQ07",
          question: "Do they avoid starting because it feels hard or because they don't want to be told?",
          measures: ["C08", "C01"],
          labels: ["Fear of Failure", "Control"]
        },
        {
          id: "AQ08",
          question: "Do they forget the steps or refuse the steps?",
          measures: ["C03", "C01"],
          labels: ["Executive Function", "Control"]
        },
        {
          id: "AQ09",
          question: "Is it worse after school or when tired?",
          measures: ["C06"],
          labels: ["Fatigue", "Emotional Regulation"]
        },
        {
          id: "AQ10",
          question: "Are sounds, clothes or food making the situation worse?",
          measures: ["C12"],
          labels: ["Sensory Sensitivity"]
        },
        {
          id: "AQ11",
          question: "Does reassurance help once, or do they keep asking?",
          measures: ["C07"],
          labels: ["Anxiety"]
        },
        {
          id: "AQ12",
          question: "Does negotiating usually work for them?",
          measures: ["C14"],
          labels: ["Learned Negotiation"]
        }
      ],

      // ------------------------------------------------------------------------
      // TABLE 7 — CHILD KNOWLEDGE BASE SCHEMA
      // This is the memory structure the app should maintain per child.
      // ------------------------------------------------------------------------

      childProfileSchema: {
        childId: "Unique identifier",
        childName: "Name",
        age: "Used for developmental expectations",
        familyId: "Links multiple children",
        activeScenarios: "Current concerns",
        observationHistory: "Behaviors observed over time",
        likelyConstructs: "Current working understanding",
        constructConfidence: "Low / Medium / High confidence",
        successfulStrategies: "What has worked previously",
        unsuccessfulStrategies: "What has not worked",
        parentNotes: "Additional context",
        sessionHistory: "Previous conversations",
        feedbackHistory: "Parent ratings of recommendations",
        lastRecommendation: "Previous action plan",
        nextLearningTargets: "What the engine still needs to learn",
        referralFlags: "Indicators for professional evaluation",
        lastUpdated: "Timestamp"
      },

      // ------------------------------------------------------------------------
      // V3 FLOW — UX + ENGINE PIPELINE
      // ------------------------------------------------------------------------

      flow: [
        "Choose Child",
        "Choose Scenario",
        "Describe What Happened",
        "Observation Recognition",
        "Map Observations to Behavioral Constructs",
        "Ask 2–5 Adaptive Questions",
        "Update Construct Confidence",
        "Generate Working Understanding",
        "Create Personalized Parenting Plan",
        "Collect Parent Feedback",
        "Update Child Profile"
      ],

      confidenceBands: {
        low: {
          min: 1,
          max: 2,
          label: "Low confidence",
          language: "This may be part of the pattern, but we need more information."
        },
        medium: {
          min: 3,
          max: 4,
          label: "Medium confidence",
          language: "This appears to be one meaningful part of the pattern."
        },
        high: {
          min: 5,
          max: 99,
          label: "High confidence",
          language: "This strongly appears to be driving the situation."
        }
      },

      referralFlags: {
        safety: [
          "repeated hitting",
          "serious aggression",
          "self-harm language",
          "running into danger",
          "threats with weapons"
        ],
        developmental: [
          "major language regression",
          "loss of previously held skills",
          "persistent school refusal",
          "extreme sensory distress"
        ],
        guidance: "When safety, development, or persistent severe distress is present, the app should recommend professional evaluation rather than only home strategies."
      }
    },

    // ==========================================================================
    // 14. OUTPUT STYLE GUIDE
    // This keeps the product light, practical, and premium.
    // ==========================================================================

    outputStyle: {
      maxSteps: 3,
      maxAvoid: 3,
      maxWhySentences: 4,
      tone: [
        "calm",
        "non-judgmental",
        "clear",
        "short",
        "practical",
        "warm"
      ],
      neverSay: [
        "your child is bad",
        "you caused this",
        "diagnosis",
        "treatment",
        "disorder",
        "therapy",
        "guaranteed",
        "cure"
      ],
      preferredPhrases: [
        "may be",
        "often",
        "try",
        "for now",
        "tonight",
        "this pattern",
        "what may be happening",
        "one small step"
      ],
      responseTemplate: {
        sections: [
          "What may be happening",
          "Try this tonight",
          "Why this fits",
          "Avoid for now",
          "What to notice"
        ]
      }
    },

    // ==========================================================================
    // 15. PUBLIC GETTERS
    // Engine can call these safely.
    // ==========================================================================


    getV3Scenario(idOrKey) {
      const scenarios = this.v3.scenarios;
      if (scenarios[idOrKey]) return scenarios[idOrKey];
      return Object.values(scenarios).find((item) => item.key === idOrKey || item.situationKey === idOrKey) || null;
    },

    listV3Scenarios() {
      return Object.keys(this.v3.scenarios).map((id) => ({
        id,
        ...this.v3.scenarios[id]
      }));
    },

    getV3ObservationCategories() {
      return this.v3.observations;
    },

    listV3Observations() {
      const output = [];
      Object.keys(this.v3.observations).forEach((categoryKey) => {
        const category = this.v3.observations[categoryKey];
        category.items.forEach((label) => {
          output.push({
            categoryKey,
            categoryLabel: category.label,
            label
          });
        });
      });
      return output;
    },

    getV3Construct(id) {
      return this.v3.constructs[id] || null;
    },

    listV3Constructs() {
      return Object.keys(this.v3.constructs).map((id) => ({
        id,
        ...this.v3.constructs[id]
      }));
    },

    getV3ConstructsForObservation(observationLabel) {
      if (!observationLabel) return [];
      const exact = this.v3.observationConstructMap[observationLabel];
      if (exact) return exact;

      const normalized = String(observationLabel).toLowerCase().replace(/["']/g, "").trim();
      const matchKey = Object.keys(this.v3.observationConstructMap).find((key) => {
        return key.toLowerCase().replace(/["']/g, "").trim() === normalized;
      });

      return matchKey ? this.v3.observationConstructMap[matchKey] : [];
    },

    getV3Intervention(constructId) {
      return this.v3.interventions[constructId] || null;
    },

    getV3AdaptiveQuestionsForConstructs(constructIds = [], limit = 5) {
      const wanted = new Set(constructIds || []);
      return this.v3.adaptiveQuestions
        .filter((question) => question.measures.some((id) => wanted.has(id)))
        .slice(0, limit);
    },

    getV3ConfidenceBand(score) {
      const value = Number(score) || 0;
      const bands = this.v3.confidenceBands;
      if (value >= bands.high.min) return bands.high;
      if (value >= bands.medium.min) return bands.medium;
      if (value >= bands.low.min) return bands.low;
      return {
        min: 0,
        max: 0,
        label: "Not enough information",
        language: "We do not have enough information about this pattern yet."
      };
    },

    buildEmptyV3ChildProfile(seed = {}) {
      return {
        childId: seed.childId || seed.id || "",
        childName: seed.childName || seed.name || "",
        age: seed.age || "",
        familyId: seed.familyId || "",
        activeScenarios: seed.activeScenarios || [],
        observationHistory: seed.observationHistory || [],
        likelyConstructs: seed.likelyConstructs || [],
        constructConfidence: seed.constructConfidence || {},
        successfulStrategies: seed.successfulStrategies || [],
        unsuccessfulStrategies: seed.unsuccessfulStrategies || [],
        parentNotes: seed.parentNotes || "",
        sessionHistory: seed.sessionHistory || [],
        feedbackHistory: seed.feedbackHistory || [],
        lastRecommendation: seed.lastRecommendation || null,
        nextLearningTargets: seed.nextLearningTargets || [],
        referralFlags: seed.referralFlags || [],
        lastUpdated: new Date().toISOString()
      };
    },

    getSituation(key) {
      return this.situations[key] || this.situations["not-listening"];
    },

    getParentResponse(key) {
      return this.parentResponses[key] || this.parentResponses.variable;
    },

    getFamilyStructure(key) {
      return this.familyStructures[key] || this.familyStructures.other;
    },

    getChildStrength(key) {
      return this.childStrengthProfiles[key] || null;
    },

    getFeedbackRule(key) {
      return this.feedbackRules[key] || this.feedbackRules.somewhat;
    },

    listSituations() {
      return Object.keys(this.situations).map((key) => ({
        key,
        label: this.situations[key].label,
        category: this.situations[key].category
      }));
    },

    listMethodologyRoles() {
      return Object.keys(this.methodologies).map((key) => ({
        key,
        publicName: this.methodologies[key].publicName,
        role: this.methodologies[key].role,
        weight: this.methodologies[key].weight
      }));
    }
  };

})(window);

/* Parenting Optimize Knowledge V3.1 MVP revisions */
(function (window) {
  "use strict";
  const PO = (window.PO = window.PO || {});
  const K = PO.Knowledge;
  if (!K || !K.v3) throw new Error("Parenting Optimize V3 knowledge must load first.");

  K.version = "3.1.0";
  K.v3.version = "3.1.0";

  /* Complete the four scenarios that previously shared or fell back to other content. */
  K.situations.negotiating = {
    label: "Negotiating Everything", category: "communication",
    likelyDrivers: ["need for influence", "high persistence", "fairness sensitivity", "learned negotiation", "inconsistent boundaries"],
    primarySkills: ["accepting limits", "respectful persuasion", "flexibility", "disappointment tolerance"],
    defaultPlan: {
      steps: ["Decide whether the limit is fixed or flexible before answering.", "Give one brief reason and one acceptable choice.", "End repeated discussion with one calm asked-and-answered phrase."],
      avoid: ["Do not turn every limit into an open-ended debate.", "Do not change the answer after prolonged arguing.", "Do not confuse thoughtful questions with permission to delay indefinitely."]
    },
    parentScript: {
      soft: "I understand why you want that. The answer is no, and you may choose what happens next.",
      firm: "I answered. This is not open for more negotiation right now.",
      repair: "You may disagree respectfully. We can discuss flexible rules at a calm time."
    }
  };

  K.situations["emotional-outbursts"] = {
    label: "Emotional Outbursts", category: "emotion",
    likelyDrivers: ["accumulated stress", "emotional sensitivity", "fatigue", "shame after correction", "difficulty recovering"],
    primarySkills: ["recognizing rising emotion", "asking for space", "recovering", "repairing communication"],
    defaultPlan: {
      steps: ["Lower stimulation and reduce words while emotion is rising.", "Offer a choice between space and quiet connection.", "Discuss the earlier trigger only after full recovery."],
      avoid: ["Do not match the child's volume.", "Do not demand an explanation during the peak.", "Do not treat every intense reaction as deliberate disrespect."]
    },
    parentScript: {
      soft: "That reaction tells me your system is overloaded. Space or quiet company?",
      firm: "I will listen when voices are safe. I am giving us a pause.",
      repair: "Something filled your cup before this moment. Let us find the first part, not only the explosion."
    }
  };

  K.situations.lying = {
    label: "Lying", category: "honesty",
    likelyDrivers: ["fear of punishment", "shame avoidance", "wishful thinking", "impulsivity", "protecting privacy or another person"],
    primarySkills: ["truth telling", "repairing mistakes", "taking responsibility", "tolerating disappointment"],
    defaultPlan: {
      steps: ["Lower the emotional stakes enough for truth to feel possible.", "Separate the original mistake from the choice to hide it.", "Focus on repair and rebuilding trust rather than labeling the child."],
      avoid: ["Do not set traps when you already know the answer.", "Do not call the child a liar.", "Do not react so strongly that honesty feels more dangerous than concealment."]
    },
    parentScript: {
      soft: "I care more about the truth than a perfect answer. Tell me what actually happened.",
      firm: "I know that version is not accurate. We need the truthful version before we can repair this.",
      repair: "Telling the truth after a mistake is how trust grows back."
    }
  };

  K.situations.anxiety = {
    label: "Anxiety / Worry", category: "emotion",
    likelyDrivers: ["uncertainty", "need for predictability", "separation concern", "fear of failure", "heightened body alarm"],
    primarySkills: ["naming worry", "tolerating uncertainty", "small brave steps", "calming the body"],
    defaultPlan: {
      steps: ["Validate that the worry feels real without confirming danger.", "Make the next brave step small and specific.", "Keep reassurance brief so it does not become the only coping tool."],
      avoid: ["Do not say there is nothing to worry about.", "Do not force the hardest version immediately.", "Do not reorganize the entire family around avoidance."]
    },
    parentScript: {
      soft: "I believe that this feels scary. We can take one small brave step together.",
      firm: "Worry can come with us, but it does not get to make every decision.",
      repair: "You handled part of something hard. That is how confidence grows."
    }
  };

  K.v3.scenarios.S05.situationKey = "negotiating";
  K.v3.scenarios.S07.situationKey = "emotional-outbursts";
  K.v3.scenarios.S10.situationKey = "lying";
  K.v3.scenarios.S11.situationKey = "anxiety";

  /* Ensure every major selectable observation can affect construct scoring. */
  Object.assign(K.v3.observationConstructMap, {
    "Ignores instructions": ["C01", "C04", "C14"],
    "Pretends not to hear": ["C04", "C14", "C01"],
    "Talks back": ["C01", "C10", "C06"],
    "Interrupts constantly": ["C04", "C11", "C13"],
    "Makes excuses": ["C08", "C14", "C03"],
    "Leaves bedroom repeatedly": ["C02", "C07", "C13", "C14"],
    "Meltdown during transitions": ["C02", "C06", "C12"],
    "Upset when plans change": ["C02", "C07", "C11"],
    "Refuses leaving friends": ["C02", "C13", "C11"],
    "Needs multiple warnings": ["C02", "C07", "C03"],
    "Refuses leaving the house": ["C02", "C07", "C01"],
    "Screams": ["C06", "C05", "C11"],
    "Slams doors": ["C06", "C01", "C11"],
    "Hits": ["C06", "C04", "C11"],
    "Kicks": ["C06", "C04", "C11"],
    "Runs away": ["C06", "C07", "C01"],
    "Withdraws": ["C05", "C07", "C13"],
    "Freezes": ["C07", "C05", "C08"],
    "Emotional overreaction": ["C06", "C05", "C07"],
    "Slow recovery after becoming upset": ["C06", "C05"],
    "Doesn't share": ["C01", "C10", "C11"],
    "Wants to win everything": ["C10", "C11", "C09"],
    "Bullies others": ["C01", "C06", "C13"],
    "Gets bullied": ["C09", "C07", "C13"],
    "Avoids children": ["C07", "C09", "C12"],
    "Difficulty making friends": ["C09", "C07", "C13"],
    "Steals": ["C01", "C04", "C13"],
    "Hides belongings": ["C07", "C08", "C14"],
    "Avoids reading": ["C08", "C09", "C03"],
    "Avoids writing": ["C08", "C09", "C03"],
    "Needs constant praise": ["C09", "C13"]
  });

  /* Age adaptation used by the result generator. */
  K.v3.ageBands = {
    toddler: { min: 2, max: 4, label: "Toddler", guidance: ["Use very short language.", "Rely on physical routines, pictures and co-regulation.", "Expect limited impulse control."] },
    earlyChildhood: { min: 5, max: 7, label: "Early Childhood", guidance: ["Use concrete choices and visual routines.", "Practice through play and repetition.", "Keep follow-through immediate and brief."] },
    elementary: { min: 8, max: 11, label: "Elementary", guidance: ["Explain the reason briefly.", "Invite limited problem solving and responsibility.", "Use measurable goals and meaningful roles."] },
    youngTeen: { min: 12, max: 15, label: "Young Teen", guidance: ["Protect dignity and privacy.", "Collaborate before conflict.", "Shift toward ownership while keeping clear safety limits."] },
    olderTeen: { min: 16, max: 18, label: "Older Teen", guidance: ["Use consultation rather than micromanagement.", "Connect freedom to responsibility.", "Focus on trust and future planning."] }
  };

  K.getV3AgeBand = function (age) {
    const value = Number(age);
    if (!Number.isFinite(value)) return null;
    return Object.values(this.v3.ageBands).find((band) => value >= band.min && value <= band.max) || null;
  };

  /* Scenario-specific construct guidance: same child pattern, different real-life plan. */
  K.v3.scenarioConstructGuidance = {
    bedtime: {
      C01: { insight: "Bedtime may intensify when it feels like a sudden loss of control.", steps: ["Let the child choose the order of two bedtime steps.", "Give the choice before resistance starts.", "Keep bedtime itself fixed."], avoid: ["Do not negotiate bedtime after the routine begins."] },
      C02: { insight: "The hardest part may be ending the current activity, not sleeping.", steps: ["Use a visible countdown.", "Create a save-it-for-tomorrow ritual.", "Use the same short sequence nightly."], avoid: ["Do not announce bedtime abruptly."] },
      C07: { insight: "Bedtime may create worry, separation discomfort or a busy mind.", steps: ["Use one predictable connection ritual.", "Name tomorrow's first reunion point.", "Practice one calming body routine."], avoid: ["Do not create endless new reassurance rituals."] }
    },
    homework: {
      C01: { insight: "Homework may have become a control contest rather than a learning task.", steps: ["Let the child choose which subject starts first.", "Set a small non-negotiable work block.", "Return ownership of the work to the child."], avoid: ["Do not hover over every answer."] },
      C03: { insight: "The child may understand the work but struggle to organize and begin it.", steps: ["Write the first three actions visibly.", "Start with materials, not the whole assignment.", "Use a short start timer."], avoid: ["Do not give a long verbal list."] },
      C08: { insight: "Avoidance may protect the child from feeling wrong or incapable.", steps: ["Choose one easy entry problem.", "Normalize mistakes before correcting them.", "Praise returning after difficulty."], avoid: ["Do not compare performance with others."] }
    },
    screens: {
      C01: { insight: "The battle may be strongest when cutoff feels imposed without prior ownership.", steps: ["Agree on the stopping point before play.", "Let the child choose the next offline activity.", "Keep the limit unchanged at cutoff."], avoid: ["Do not invent the rule during shutdown."] },
      C02: { insight: "The abrupt contrast between a high-reward screen and ordinary life may drive the conflict.", steps: ["Use a visible countdown.", "Finish at a natural stopping point when possible.", "Move directly into a prepared physical or social activity."], avoid: ["Do not leave an empty transition."] },
      C11: { insight: "A persistent child may experience unfinished play as an open loop.", steps: ["Use a screenshot or save-state ritual.", "Name when play can resume.", "Use one consistent closing phrase."], avoid: ["Do not enter repeated five-more-minute negotiations."] }
    },
    siblings: {
      C01: { insight: "Leadership energy may be appearing as control over the sibling.", steps: ["Protect ownership of important belongings.", "Teach a direct request for space.", "Give leadership roles that do not control another child."], avoid: ["Do not make one child responsible for policing another."] },
      C10: { insight: "Conflict may be fueled by intense monitoring of who received more.", steps: ["Separate equal from fair.", "Use clear turn-taking rules.", "Hear both perspectives before repair."], avoid: ["Do not automatically tell the older child to give in."] },
      C13: { insight: "Conflict may be the child's fastest route to parental attention.", steps: ["Add predictable one-to-one connection.", "Notice cooperative moments.", "Keep conflict attention calm and brief."], avoid: ["Do not give the biggest reaction to the worst behavior."] }
    },
    negotiating: {
      C01: { insight: "The child may be trying to preserve influence rather than simply reject the rule.", steps: ["Separate fixed limits from flexible details.", "Offer one meaningful choice.", "Schedule calm times when real negotiation is welcome."], avoid: ["Do not make every decision rigid or endlessly negotiable."] },
      C10: { insight: "The child may be objecting to the logic or fairness of the rule.", steps: ["Give one clear reason.", "Acknowledge the fairness concern.", "Keep the limit if it still stands."], avoid: ["Do not use fairness rules inconsistently."] },
      C14: { insight: "Persistence may continue because it has changed the answer before.", steps: ["Use one final-answer phrase.", "Stop adding attention after it.", "Reinforce respectful acceptance later."], avoid: ["Do not give in at the end of a long argument."] }
    },
    lying: {
      C07: { insight: "The child may believe the truth will trigger an overwhelming reaction.", steps: ["Lower your intensity.", "State that truth allows repair.", "Respond proportionately to the original mistake."], avoid: ["Do not make honesty emotionally unsafe."] },
      C08: { insight: "The lie may protect the child from shame about failing or making a mistake.", steps: ["Separate worth from performance.", "Ask for the accurate version without humiliation.", "Create one concrete repair step."], avoid: ["Do not use global labels."] },
      C14: { insight: "Concealment may continue because it has delayed consequences before.", steps: ["Verify key responsibilities calmly.", "Use predictable repair when information is hidden.", "Reinforce early honesty."], avoid: ["Do not reward a long false story."] }
    },
    anxiety: {
      C07: { insight: "The child's alarm system may be treating uncertainty as danger.", steps: ["Name the worry.", "Choose one small brave action.", "Keep reassurance brief and repeatable."], avoid: ["Do not promise absolute certainty."] },
      C13: { insight: "The child may borrow calm through repeated closeness and reassurance.", steps: ["Offer predictable connection.", "Pair connection with a small independent step.", "Praise coping, not only comfort seeking."], avoid: ["Do not suddenly withdraw support or provide unlimited reassurance."] },
      C08: { insight: "Worry may center on being wrong, embarrassed or unsuccessful.", steps: ["Normalize imperfect attempts.", "Preview the first manageable step.", "Avoid performance comparisons."], avoid: ["Do not make bravery depend on perfect success."] }
    }
  };

  K.getV3ScenarioConstructGuidance = function (scenarioKey, constructId) {
    const scenario = this.v3.scenarioConstructGuidance[scenarioKey];
    return scenario ? scenario[constructId] || null : null;
  };

  /* Scored adaptive questions so answers genuinely change the inferred profile. */
  K.v3.adaptiveQuestions = [
    { id: "AQ01", question: "When plans change unexpectedly, what usually happens?", measures: ["C02", "C07", "C11"], options: [
      { label: "They adjust fairly easily", scores: { C02: -1, C07: -1 } },
      { label: "They need warning, but recover", scores: { C02: 1 } },
      { label: "They push hard for the original plan", scores: { C02: 2, C11: 2, C01: 1 } },
      { label: "They become worried or very upset", scores: { C02: 2, C07: 2 } }
    ]},
    { id: "AQ02", question: "When your child hears no, what happens first?", measures: ["C01", "C10", "C11", "C14", "C06"], options: [
      { label: "They accept it, even if disappointed", scores: { C01: -1, C14: -1 } },
      { label: "They calmly try to persuade me", scores: { C01: 2, C11: 2, C10: 1 } },
      { label: "They become emotionally overwhelmed", scores: { C06: 2, C07: 1 } },
      { label: "They keep asking because it sometimes works", scores: { C14: 3, C15: 1 } }
    ]},
    { id: "AQ03", question: "When you offer two acceptable choices, does cooperation change?", measures: ["C01", "C03"], options: [
      { label: "It becomes much easier", scores: { C01: 3 } },
      { label: "It helps a little", scores: { C01: 1 } },
      { label: "It makes no difference", scores: { C01: -1 } },
      { label: "Too many choices make things harder", scores: { C03: 2, C07: 1 } }
    ]},
    { id: "AQ04", question: "Does this pattern happen the same way with both parents or caregivers?", measures: ["C15", "C14"], options: [
      { label: "Yes, it is very similar", scores: { C15: -1 } },
      { label: "Mostly with one adult", scores: { C15: 2, C14: 1 } },
      { label: "The adults usually respond differently", scores: { C15: 3 } },
      { label: "There is only one regular caregiver", scores: {} }
    ]},
    { id: "AQ05", question: "Once upset, what helps your child recover fastest?", measures: ["C05", "C06", "C13"], options: [
      { label: "Quiet closeness", scores: { C13: 2, C05: 1 } },
      { label: "Physical space", scores: { C06: 1 } },
      { label: "Movement or sensory calming", scores: { C12: 2, C06: 1 } },
      { label: "Very little seems to help quickly", scores: { C06: 3 } }
    ]},
    { id: "AQ06", question: "When your child loses a game or gets something wrong, what stands out most?", measures: ["C06", "C08", "C09", "C10", "C11"], options: [
      { label: "They want to try again", scores: { C11: 2, C09: -1 } },
      { label: "They argue that it was unfair", scores: { C10: 3 } },
      { label: "They become very upset", scores: { C06: 2, C05: 1 } },
      { label: "They quit or avoid trying again", scores: { C08: 3, C09: 2 } }
    ]},
    { id: "AQ07", question: "When a task is difficult, what usually stops your child from starting?", measures: ["C01", "C03", "C08", "C09"], options: [
      { label: "They do not know where to begin", scores: { C03: 3 } },
      { label: "They are afraid of getting it wrong", scores: { C08: 3, C09: 1 } },
      { label: "They resist being told to do it", scores: { C01: 3 } },
      { label: "They start but quickly lose focus", scores: { C04: 3, C03: 1 } }
    ]},
    { id: "AQ08", question: "When you give several steps, what usually happens?", measures: ["C03", "C04", "C01"], options: [
      { label: "They remember and complete them", scores: { C03: -2 } },
      { label: "They forget partway through", scores: { C03: 3 } },
      { label: "They get distracted by something else", scores: { C04: 3 } },
      { label: "They remember but refuse", scores: { C01: 2 } }
    ]},
    { id: "AQ09", question: "When is this behavior usually at its worst?", measures: ["C05", "C06", "C07", "C12"], options: [
      { label: "After school or late in the day", scores: { C06: 2, C05: 1 } },
      { label: "During noisy or crowded situations", scores: { C12: 3 } },
      { label: "Before uncertain or demanding events", scores: { C07: 3 } },
      { label: "It is similar throughout the day", scores: {} }
    ]},
    { id: "AQ10", question: "Do sounds, clothes, food textures, light or touch make the situation harder?", measures: ["C12"], options: [
      { label: "Often", scores: { C12: 3 } },
      { label: "Sometimes", scores: { C12: 1 } },
      { label: "Rarely", scores: { C12: -1 } },
      { label: "I am not sure yet", scores: {} }
    ]},
    { id: "AQ11", question: "When you reassure your child, what happens next?", measures: ["C07", "C13"], options: [
      { label: "They settle and move forward", scores: { C07: 1, C13: 1 } },
      { label: "They ask the same question again soon", scores: { C07: 3, C13: 1 } },
      { label: "They need physical closeness more than answers", scores: { C13: 3 } },
      { label: "Reassurance does not seem relevant", scores: { C07: -1, C13: -1 } }
    ]},
    { id: "AQ12", question: "When your child keeps negotiating, how often does the final answer eventually change?", measures: ["C14", "C15"], options: [
      { label: "Often", scores: { C14: 3, C15: 2 } },
      { label: "Sometimes", scores: { C14: 2, C15: 1 } },
      { label: "Almost never", scores: { C14: -1 } },
      { label: "It depends on which adult is present", scores: { C15: 3, C14: 2 } }
    ]}
  ];

  K.scoreV3ConstructsFromObservations = function (observations) {
    const scores = {};
    (observations || []).forEach((observation) => {
      const label = typeof observation === "string" ? observation : observation.label || observation.observation;
      this.getV3ConstructsForObservation(label).forEach((id, index) => {
        scores[id] = (scores[id] || 0) + Math.max(1, 3 - index);
      });
    });
    return scores;
  };

  K.applyV3QuestionScores = function (baseScores, questionId, optionIndex) {
    const next = Object.assign({}, baseScores || {});
    const question = this.v3.adaptiveQuestions.find((item) => item.id === questionId);
    if (!question || !question.options || !question.options[optionIndex]) return next;
    const scores = question.options[optionIndex].scores || {};
    Object.keys(scores).forEach((id) => {
      next[id] = Math.max(0, (next[id] || 0) + scores[id]);
    });
    return next;
  };

  K.rankV3Constructs = function (scores, limit) {
    return Object.keys(scores || {})
      .map((id) => ({ id, score: scores[id], construct: this.getV3Construct(id) }))
      .filter((item) => item.construct)
      .sort((a, b) => b.score - a.score)
      .slice(0, limit || 4);
  };
})(window);

/* ========================================================================== 
 * Parenting Optimize Knowledge — V3.3 Paid Blueprint Expansion
 * Adds the deep-report knowledge layer used by the paid Parenting Blueprint.
 * This section is additive and preserves the complete V3.2 knowledge base.
 * ========================================================================== */
(function (window) {
  "use strict";

  const PO = (window.PO = window.PO || {});
  const K = PO.Knowledge;

  if (!K || !K.v3) {
    throw new Error("Parenting Optimize knowledge V3 must load before V3.3.");
  }

  K.version = "3.3.0";
  K.v3.version = "3.3.0";

  K.v3.paidBlueprint = {
    version: "3.3.0",
    productName: "Personalized Parenting Blueprint",
    freeSections: [
      "patternSummary",
      "topDrivers",
      "confidencePreview",
      "oneImmediateInsight"
    ],
    paidSections: [
      "childProfile",
      "howWeReachedThis",
      "situationAnalysis",
      "parentChildLoop",
      "beforeDuringAfterPlan",
      "scripts",
      "exampleConversation",
      "motivationAndRewards",
      "coParenting",
      "sevenDayPlan",
      "progressMarkers",
      "alternateExplanation",
      "ifPlanFails",
      "professionalSupport"
    ],
    disclaimer: "Educational guidance only. This Blueprint is not a diagnosis, treatment plan, or substitute for professional care.",
    confidenceLanguage: {
      low: "We see an early possibility, but more observation is needed before treating it as the main pattern.",
      medium: "Several answers point in this direction, although another explanation may still be contributing.",
      high: "Multiple independent answers point toward the same pattern, making it a strong working explanation."
    }
  };

  K.v3.constructBlueprints = {
    C01: {
      name: "Need for Control",
      profile: "This child may cooperate best when they retain a meaningful amount of ownership. Direct pressure can quickly turn an ordinary request into a contest over who decides.",
      strengths: ["independence", "leadership", "initiative", "strong opinions"],
      stressTriggers: ["abrupt commands", "feeling cornered", "too many adult decisions", "public correction"],
      motivation: ["meaningful choices", "real responsibility", "being consulted", "visible ownership"],
      rewardIdeas: ["choose the order of activities", "lead a short family task", "select between two approved privileges", "earn responsibility rather than only prizes"],
      rewardBackfires: ["bribing after refusal", "open-ended bargaining", "rewards that invite a second negotiation"],
      warmScript: "I know you want a say. You can choose between these two options, and the limit stays the same.",
      firmScript: "You may choose how to do it. Whether it happens is not optional.",
      repairScript: "We both got pulled into a control battle. Next time I will give you a choice earlier, and you will practice accepting the boundary.",
      insteadOf: "Because I said so. Stop arguing.",
      tryInstead: "I will explain once. Then you can choose A or B.",
      ifFails: ["reduce the number of choices", "offer the choice earlier", "check whether anxiety rather than autonomy is driving the resistance"],
      coParenting: "Both adults should agree which parts are fixed and which parts the child may control. Mixed messages make negotiation more rewarding.",
      progress: ["accepts one of two choices faster", "less time spent debating", "uses leadership appropriately", "recovers after hearing no"]
    },
    C02: {
      name: "Transition Difficulty",
      profile: "The behavior may begin when the child has to stop one state and enter another. The problem is often the shift itself, not the destination.",
      strengths: ["deep engagement", "focus", "loyalty to activities", "ability to immerse"],
      stressTriggers: ["surprise changes", "unfinished activities", "rushing", "several transitions close together"],
      motivation: ["advance notice", "visible endings", "save-state rituals", "predictable sequence"],
      rewardIdeas: ["choose the transition song", "mark successful transitions", "earn control over the next neutral detail", "celebrate faster recovery"],
      rewardBackfires: ["rewarding only perfect transitions", "threatening during the countdown", "changing the ending repeatedly"],
      warmScript: "Stopping is hard when you are deeply involved. I will help you save your place, then we are moving on.",
      firmScript: "The countdown is finished. The first step is standing up.",
      repairScript: "The change came fast and your body got stuck. Tomorrow we will use the same warning and first step.",
      insteadOf: "Why is everything such a big deal? Just move.",
      tryInstead: "Two minutes left. Decide what you want to save for later.",
      ifFails: ["lengthen the warning", "reduce words", "make the first physical step smaller", "check sensory overload or anxiety"],
      coParenting: "Use the same countdown language and do not let one adult restart an activity after the other has ended it.",
      progress: ["begins moving after one warning", "needs fewer prompts", "uses a save-state ritual", "recovers faster after an unexpected change"]
    },
    C03: {
      name: "Executive Function Difficulty",
      profile: "The child may know what to do but struggle to hold the steps, organize the sequence, or begin without external structure.",
      strengths: ["creative thinking", "big-picture ideas", "spontaneity", "problem solving when engaged"],
      stressTriggers: ["multi-step directions", "clutter", "time pressure", "vague requests"],
      motivation: ["visible steps", "one action at a time", "short timers", "clear finish lines"],
      rewardIdeas: ["check off completed steps", "beat their own routine time", "earn autonomy after reliable follow-through", "choose the checklist format"],
      rewardBackfires: ["punishing forgetfulness as laziness", "too many tracking systems", "long delayed rewards"],
      warmScript: "You do not have to remember everything. Let’s look at the first step together.",
      firmScript: "One step now: put the folder in the bag.",
      repairScript: "The directions were too large. Next time we will use a short list instead of repeating.",
      insteadOf: "I told you five times. Why can’t you remember anything?",
      tryInstead: "Show me the first item on your list.",
      ifFails: ["simplify to one step", "change verbal directions to visual cues", "remove distractions", "check task skill level"],
      coParenting: "Adults should use one shared checklist rather than giving different verbal instructions.",
      progress: ["starts after one cue", "completes more steps independently", "loses fewer materials", "asks for clarification before getting stuck"]
    },
    C04: {
      name: "Attention Regulation",
      profile: "The child’s attention may be captured elsewhere or may drift before the instruction becomes action. This can look like ignoring even when there is no deliberate refusal.",
      strengths: ["intense focus on interests", "curiosity", "quick noticing", "energy"],
      stressTriggers: ["instructions from a distance", "background noise", "long explanations", "boring repetitive tasks"],
      motivation: ["close-range cues", "brief instructions", "movement", "immediate feedback"],
      rewardIdeas: ["short focus sprints", "movement breaks", "visible streaks", "choice of active responsibility"],
      rewardBackfires: ["repeated yelling", "very long tasks", "removing all movement"],
      warmScript: "I’m coming close so your brain can catch the instruction.",
      firmScript: "Eyes here for one sentence. Shoes on now.",
      repairScript: "I was calling from far away. Next time I will get close before expecting action.",
      insteadOf: "You heard me. Stop pretending.",
      tryInstead: "Tell me the one thing you are doing next.",
      ifFails: ["remove one distraction", "add movement", "shorten the work interval", "check executive function demands"],
      coParenting: "Use the same attention cue and avoid one adult repeating while another gives new directions.",
      progress: ["responds to close cue", "sustains short work periods", "repeats instructions accurately", "needs fewer reminders"]
    },
    C05: {
      name: "Emotional Sensitivity",
      profile: "This child may register tone, criticism, disappointment, or conflict more intensely than others. The emotional impact can become larger than the original correction.",
      strengths: ["empathy", "care", "emotional awareness", "strong relationships"],
      stressTriggers: ["sharp tone", "public correction", "sarcasm", "feeling misunderstood"],
      motivation: ["private correction", "warmth", "specific reassurance", "repair after conflict"],
      rewardIdeas: ["private praise", "meaningful connection time", "kindness roles", "recognition of emotional effort"],
      rewardBackfires: ["public praise that embarrasses", "teasing", "withholding connection"],
      warmScript: "I can see that landed hard. You are still safe and loved, and we still need to fix the behavior.",
      firmScript: "I will speak calmly, and the boundary still stands.",
      repairScript: "My tone was stronger than I intended. The correction matters, and so does repairing how it felt.",
      insteadOf: "You are too sensitive.",
      tryInstead: "Your feelings are real. Let’s separate the feeling from the next action.",
      ifFails: ["reduce audience", "lower tone", "delay teaching until calm", "check anxiety or shame"],
      coParenting: "Both adults should avoid using the child’s sensitivity as a criticism and should repair quickly after harsh moments.",
      progress: ["recovers faster from correction", "uses words instead of shutting down", "accepts private feedback", "separates mistake from self-worth"]
    },
    C06: {
      name: "Emotional Dysregulation",
      profile: "Once activated, the child may temporarily lose access to flexible thinking, respectful language, and problem solving. Regulation must come before teaching.",
      strengths: ["passion", "intensity", "enthusiasm", "strong emotional expression"],
      stressTriggers: ["fatigue", "hunger", "stacked demands", "lectures during escalation"],
      motivation: ["co-regulation", "predictable calm-down routine", "few words", "repair after recovery"],
      rewardIdeas: ["reward recovery skills", "notice use of a pause", "track shorter recovery", "choose calming tools"],
      rewardBackfires: ["rewarding only no emotion", "threats during peak", "long consequences added in anger"],
      warmScript: "Your feelings are very big right now. I am here, and we will solve this after your body settles.",
      firmScript: "I will not let anyone be hurt. We are pausing now.",
      repairScript: "The feeling was real, and the behavior still needs repair. Let’s practice the first warning sign.",
      insteadOf: "Calm down right now or else.",
      tryInstead: "Fewer words now. We will talk when your body is ready.",
      ifFails: ["increase physical safety", "reduce stimulation", "track earlier triggers", "seek professional support for frequent severe episodes"],
      coParenting: "One adult should lead calmly while the other reduces stimulation and avoids adding new demands.",
      progress: ["recognizes early body signs", "uses a pause", "shorter recovery time", "repairs after the episode"]
    },
    C07: {
      name: "Anxiety / Worry",
      profile: "Fear or uncertainty may be driving avoidance, repeated questions, physical complaints, or resistance. The goal is support plus gradual courage, not endless reassurance or forced exposure.",
      strengths: ["careful thinking", "awareness", "preparation", "sensitivity to risk"],
      stressTriggers: ["uncertainty", "separation", "performance pressure", "unexpected change"],
      motivation: ["predictability", "small brave steps", "brief reassurance", "clear coping plan"],
      rewardIdeas: ["praise brave action", "track tolerated uncertainty", "choose a coping tool", "celebrate approach rather than outcome"],
      rewardBackfires: ["paying for total absence of worry", "reassuring repeatedly", "allowing complete avoidance"],
      warmScript: "I believe this feels scary. We can take one small brave step together.",
      firmScript: "Worry can come with us, but it does not make every decision.",
      repairScript: "I pushed too fast. Next time we will make the brave step smaller, not remove it completely.",
      insteadOf: "There is nothing to worry about.",
      tryInstead: "What is the smallest part you can do while feeling worried?",
      ifFails: ["shrink the exposure step", "use body calming first", "reduce repeated reassurance", "consider professional support if life becomes restricted"],
      coParenting: "Agree on one reassurance phrase and one next step so the child does not seek different certainty from each adult.",
      progress: ["asks fewer repeated questions", "approaches with support", "tolerates uncertainty longer", "uses coping tools independently"]
    },
    C08: {
      name: "Fear of Failure",
      profile: "Avoidance may protect the child from the shame of being wrong, slow, or imperfect. Lowering the emotional cost of mistakes can unlock effort.",
      strengths: ["high standards", "care about quality", "thoughtfulness", "desire to succeed"],
      stressTriggers: ["comparison", "public mistakes", "hard first steps", "constant correction"],
      motivation: ["small entry task", "effort praise", "normalizing mistakes", "private support"],
      rewardIdeas: ["reward starting", "celebrate corrections", "track attempts", "choose challenge level"],
      rewardBackfires: ["rewarding only perfect performance", "comparison with siblings", "taking over the task"],
      warmScript: "You do not have to prove you can do all of it. We are only practicing the first part.",
      firmScript: "Avoiding it will not make it disappear. We are starting with one manageable piece.",
      repairScript: "I focused too much on the result. Next time I will notice courage and persistence too.",
      insteadOf: "You are being lazy.",
      tryInstead: "Show me the part that feels most risky to get wrong.",
      ifFails: ["make task smaller", "begin together", "check actual skill deficit", "reduce evaluative pressure"],
      coParenting: "Use the same effort-focused language and avoid one parent rescuing while the other pressures.",
      progress: ["starts sooner", "tolerates corrections", "tries again after error", "asks for help rather than avoiding"]
    },
    C09: {
      name: "Low Confidence",
      profile: "The child may doubt their ability before testing it. They need believable evidence of competence, not only general praise.",
      strengths: ["humility", "carefulness", "openness to support", "awareness of limits"],
      stressTriggers: ["public evaluation", "large challenges", "vague praise", "comparison"],
      motivation: ["small wins", "specific praise", "scaffolding", "visible progress"],
      rewardIdeas: ["competence milestones", "choice after completion", "showcase private progress", "meaningful responsibility"],
      rewardBackfires: ["empty reassurance", "overhelping", "public spotlight"],
      warmScript: "You may not feel ready yet, but you have handled the first step before.",
      firmScript: "I will help you begin, but I will not do the part you can practice.",
      repairScript: "I stepped in too quickly. Next time I will give you more room to show what you can do.",
      insteadOf: "Of course you can do it. Stop being silly.",
      tryInstead: "Which part do you already know how to do?",
      ifFails: ["lower challenge", "make praise more specific", "review previous success", "check fear of failure"],
      coParenting: "Adults should avoid contrasting the child with a more confident sibling and should agree where to help versus step back.",
      progress: ["attempts before asking for rescue", "names own strengths", "accepts manageable challenge", "recovers from mistakes"]
    },
    C10: {
      name: "Fairness Sensitivity",
      profile: "The child may closely monitor rules, turn-taking, and differences in treatment. This can reflect moral awareness, but it can also keep conflicts alive.",
      strengths: ["justice awareness", "rule consciousness", "advocacy", "attention to consistency"],
      stressTriggers: ["unequal outcomes", "unexplained exceptions", "sibling comparison", "changing rules"],
      motivation: ["brief rationale", "consistent process", "clear difference between equal and fair", "chance to be heard"],
      rewardIdeas: ["help design fair procedures", "lead turn-taking", "recognize respectful advocacy", "earn trust through consistency"],
      rewardBackfires: ["debating every fairness claim", "making promises adults cannot keep", "automatic equal treatment when needs differ"],
      warmScript: "I hear that this feels unfair. I will explain the rule once and listen to one concern.",
      firmScript: "You may disagree. The decision is still finished for now.",
      repairScript: "I want you to speak up about fairness respectfully. We will practice doing that without extending the conflict.",
      insteadOf: "Life isn’t fair. Get over it.",
      tryInstead: "Equal and fair are not always the same. Here is the reason for this decision.",
      ifFails: ["shorten explanations", "check control-seeking", "use written family rules", "schedule fairness discussions outside conflict"],
      coParenting: "Adults should use the same explanation for common family rules and avoid promising identical outcomes for every child.",
      progress: ["states concern once", "accepts explanation", "uses respectful advocacy", "recovers when outcome remains unequal"]
    },
    C11: {
      name: "High Persistence",
      profile: "The child may keep pursuing a goal or objection long after others would stop. This can become determination when boundaries and closure are clear.",
      strengths: ["determination", "grit", "follow-through", "leadership"],
      stressTriggers: ["unfinished goals", "unclear stopping point", "adult inconsistency", "open debates"],
      motivation: ["closure ritual", "clear finish line", "future return point", "meaningful challenge"],
      rewardIdeas: ["longer projects", "save-state systems", "responsibility requiring follow-through", "recognition for stopping appropriately"],
      rewardBackfires: ["giving in after prolonged pressure", "unclear maybe answers", "endless debate"],
      warmScript: "I know you want to keep going. We are closing this now and returning to it at the agreed time.",
      firmScript: "The discussion is finished. Persistence will not change the answer.",
      repairScript: "Your determination is valuable. We are practicing where determination helps and where stopping is the skill.",
      insteadOf: "Why can’t you ever let anything go?",
      tryInstead: "You may save your idea for tomorrow. Tonight the decision is closed.",
      ifFails: ["make closure more concrete", "avoid re-engaging", "check learned negotiation", "provide a future return point"],
      coParenting: "Both adults must recognize the same stopping point; otherwise persistence is naturally reinforced.",
      progress: ["accepts closure phrase", "returns to issue at agreed time", "uses persistence on productive tasks", "shorter repeated asking"]
    },
    C12: {
      name: "Sensory Sensitivity",
      profile: "Noise, clothing, light, food texture, touch, or crowded environments may be increasing distress before the behavior becomes visible.",
      strengths: ["fine noticing", "aesthetic awareness", "body awareness", "sensitivity to environment"],
      stressTriggers: ["loud spaces", "uncomfortable fabric", "unexpected touch", "strong smells or textures"],
      motivation: ["reasonable adjustments", "advance preparation", "choice of acceptable materials", "lower stimulation"],
      rewardIdeas: ["choose sensory tools", "plan a calm space", "earn independence managing needs", "track successful coping"],
      rewardBackfires: ["forcing discomfort as proof", "mocking complaints", "removing all exposure without skill-building"],
      warmScript: "I believe your body is uncomfortable. Let’s find the adjustment that still lets us complete the next step.",
      firmScript: "We can change the fabric or the sound level. We still need an acceptable option.",
      repairScript: "I thought you were avoiding. Next time I will check the sensory part before assuming.",
      insteadOf: "You are being dramatic.",
      tryInstead: "Which part feels wrong: sound, touch, light, smell, or something else?",
      ifFails: ["reduce stimulation", "offer two alternatives", "track patterns", "consider occupational or developmental evaluation for severe persistent distress"],
      coParenting: "Agree which accommodations are reasonable and keep them consistent rather than one adult forcing and the other removing all demands.",
      progress: ["names sensory trigger", "chooses an alternative", "uses coping tool", "participates with adjustment"]
    },
    C13: {
      name: "Connection Seeking",
      profile: "The child may use delay, distress, or conflict to secure closeness or reassurance. Connection before correction can reduce the need to seek it through behavior.",
      strengths: ["relational awareness", "affection", "loyalty", "desire for closeness"],
      stressTriggers: ["separation", "adult distraction", "correction without repair", "feeling replaced"],
      motivation: ["predictable connection", "brief one-to-one time", "warm limit", "repair"],
      rewardIdeas: ["special time", "shared task", "connection ritual", "responsibility that creates belonging"],
      rewardBackfires: ["attention only after escalation", "withdrawing affection", "unlimited reassurance"],
      warmScript: "I see you and I am staying connected. First we complete this step, then we have our time.",
      firmScript: "You do not need to create a crisis to get my attention. I will be with you after the next step.",
      repairScript: "We got disconnected. Let’s reconnect now without changing the boundary.",
      insteadOf: "You are only doing this for attention.",
      tryInstead: "You need connection. I can give connection and still hold the limit.",
      ifFails: ["schedule predictable connection", "reduce emotional reaction to misbehavior", "check anxiety", "reinforce calm bids for attention"],
      coParenting: "Coordinate one-to-one time so connection does not depend on which adult is easier to pull into the conflict.",
      progress: ["asks directly for connection", "less escalation for attention", "accepts delayed connection", "repairs after conflict"]
    },
    C14: {
      name: "Learned Negotiation",
      profile: "The child may have learned that persistence, distress, or repeated requests sometimes changes the outcome. The pattern is logical from the child’s perspective.",
      strengths: ["persuasion", "verbal skill", "persistence", "problem solving"],
      stressTriggers: ["unclear limits", "maybe answers", "adult fatigue", "different adult responses"],
      motivation: ["clear answer", "predictable follow-through", "scheduled negotiation", "reward for respectful acceptance"],
      rewardIdeas: ["earn a future request opportunity", "recognize accepting no", "reward respectful persuasion at appropriate times", "offer planned flexibility"],
      rewardBackfires: ["giving in after escalation", "adding explanations with every objection", "threats not enforced"],
      warmScript: "You may ask once and I will answer. After that, continuing will not change it.",
      firmScript: "Asked and answered. We are moving on.",
      repairScript: "I changed the answer after too much arguing. That taught the wrong lesson. Next time I will be clearer.",
      insteadOf: "Fine, just this once, but stop asking.",
      tryInstead: "The answer is no today. We can discuss a future option tomorrow.",
      ifFails: ["reduce explanations", "align adults", "hold one small limit consistently", "check whether need for control is also present"],
      coParenting: "The adults must use the same final answer and avoid reversing each other after persistence.",
      progress: ["asks once", "accepts final answer sooner", "uses negotiation at agreed times", "less escalation after no"]
    },
    C15: {
      name: "Inconsistent Boundaries",
      profile: "The child may be responding to changing rules, different adults, or limits that depend on mood and energy. Testing becomes a rational way to discover today’s real boundary.",
      strengths: ["social awareness", "adaptability", "ability to read adults", "persistence"],
      stressTriggers: ["different answers", "rules created mid-conflict", "good-cop/bad-cop dynamics", "unpredictable consequences"],
      motivation: ["simple shared rules", "same wording", "private adult alignment", "reliable follow-through"],
      rewardIdeas: ["recognize first-time cooperation", "family rule streak", "earn flexibility through reliability", "participate in calm rule-setting"],
      rewardBackfires: ["one adult rescuing", "rules changing after protest", "complicated consequence systems"],
      warmScript: "The adults have agreed on this rule. You will hear the same answer from both of us.",
      firmScript: "Asking another adult will not change the decision.",
      repairScript: "We gave mixed signals. The adults will fix that, and the rule from now on is clear.",
      insteadOf: "Go ask your mother/father.",
      tryInstead: "We will discuss it privately and give you one answer.",
      ifFails: ["choose fewer rules", "write shared scripts", "remove consequences adults cannot enforce", "address adult disagreement privately"],
      coParenting: "This construct is itself a co-parenting issue: align privately, use one sentence, and never negotiate adult disagreement through the child.",
      progress: ["less parent-shopping", "fewer repeated tests", "adults use same wording", "rules hold even under fatigue"]
    }
  };

  K.v3.scenarioBlueprints = {
    S01: {
      title: "Bedtime Struggles",
      commonLoop: ["Parent announces bedtime", "Child delays or seeks one more thing", "Parent repeats or negotiates", "Child learns bedtime remains open", "Both become more activated"],
      before: ["Set the stopping point before the final activity", "Use the same short routine", "Prepare one connection ritual"],
      during: ["Use one choice inside the boundary", "Keep language brief", "Do not reopen completed steps"],
      after: ["Praise recovery or staying in bed", "Repair briefly the next morning", "Adjust timing if overtiredness contributed"],
      examples: [
        { instead: "Go to bed right now. How many times do I have to tell you?", try: "Bedtime has started. Teeth first or pajamas first?" },
        { instead: "Fine, one more story, but this is the last time.", try: "Stories are finished. I will sit with you for one quiet minute." }
      ]
    },
    S02: {
      title: "Morning Routine",
      commonLoop: ["Parent gives several directions", "Child stalls or forgets", "Parent repeats with urgency", "Child becomes slower or oppositional", "Everyone leaves stressed"],
      before: ["Prepare clothes and bags the night before", "Use one visible sequence", "Reduce optional decisions"],
      during: ["Give one step", "Use a neutral time cue", "Check at one planned point"],
      after: ["Notice what improved", "Reset materials", "Change only one weak point at a time"],
      examples: [
        { instead: "Get dressed, brush teeth, pack your bag and hurry up.", try: "First clothes. Check the chart when you are done." },
        { instead: "We are late because of you.", try: "We have five minutes. I will help with the first step, then you continue." }
      ]
    },
    S03: {
      title: "Homework Refusal",
      commonLoop: ["Parent presents the whole assignment", "Child anticipates difficulty", "Avoidance begins", "Parent increases pressure or help", "Homework becomes a relationship battle"],
      before: ["Create a predictable start time", "Prepare materials", "Choose a short first work block"],
      during: ["Start with one small item", "Praise returning after difficulty", "Separate help from taking over"],
      after: ["Mark what was completed", "Note where skill gaps appeared", "End with a clear closure"],
      examples: [
        { instead: "Sit down and finish all of this now.", try: "Open the book and choose the first problem. We are starting with ten minutes." },
        { instead: "You know this. Stop acting helpless.", try: "Show me the point where it stops making sense." }
      ]
    },
    S04: {
      title: "Doesn't Listen",
      commonLoop: ["Parent calls from a distance", "Child remains absorbed", "Parent repeats", "Child learns the first requests are optional", "Parent eventually escalates"],
      before: ["Decide the one instruction", "Reduce competing noise", "Move close"],
      during: ["Get attention", "Say one sentence", "Follow through after one reminder"],
      after: ["Notice faster response", "Practice routine steps", "Use a visual cue for repeated tasks"],
      examples: [
        { instead: "I have told you six times!", try: "Eyes here. Shoes on now." },
        { instead: "Are you deaf?", try: "Tell me what you heard me ask." }
      ]
    },
    S05: {
      title: "Negotiates Everything",
      commonLoop: ["Parent sets a limit", "Child presents counteroffers", "Parent explains repeatedly", "The discussion becomes the reward", "The limit weakens or everyone escalates"],
      before: ["Decide what is fixed", "Choose what is genuinely flexible", "Use a final-answer phrase"],
      during: ["Acknowledge once", "Offer one acceptable choice", "End circular discussion"],
      after: ["Praise respectful acceptance", "Schedule real negotiation for calm times", "Review whether adults stayed aligned"],
      examples: [
        { instead: "Let me explain again why you cannot.", try: "I heard your idea. The answer is still no for tonight." },
        { instead: "Fine, five minutes, but stop asking.", try: "Asking again will not change the stopping point." }
      ]
    },
    S06: {
      title: "Tantrums / Meltdowns",
      commonLoop: ["Stress builds", "Child loses regulation", "Adults add words or consequences", "Stimulation increases", "Recovery takes longer"],
      before: ["Track fatigue, hunger and sensory load", "Prepare a calm space", "Teach a pause signal"],
      during: ["Protect safety", "Use very few words", "Reduce stimulation"],
      after: ["Reconnect", "Name one trigger", "Practice one alternative response"],
      examples: [
        { instead: "You need to explain why you are doing this right now.", try: "You are flooded. We will talk when your body is calmer." },
        { instead: "If you throw that again, you lose everything.", try: "I will keep everyone safe. I am moving the object away." }
      ]
    },
    S07: {
      title: "Emotional Outbursts",
      commonLoop: ["Stress accumulates", "Small trigger releases it", "Adult reacts only to the final event", "Child feels misunderstood", "Outburst intensifies"],
      before: ["Build decompression time", "Notice early signs", "Reduce stacked demands"],
      during: ["Lower tone", "Offer space or quiet connection", "Pause the original discussion"],
      after: ["Look backward for accumulated stress", "Repair language", "Plan for the first warning sign"],
      examples: [
        { instead: "You are exploding over nothing.", try: "This looks bigger than the last trigger. We will figure out what filled your cup." },
        { instead: "Fix your attitude.", try: "Pause first. We will return to the problem when your voice is safe." }
      ]
    },
    S08: {
      title: "Screen Time Battles",
      commonLoop: ["Screen begins without clear ending", "Adult announces stop during high engagement", "Child protests", "Time is extended or conflict escalates", "Next cutoff becomes harder"],
      before: ["Set the rule before starting", "Choose a natural stopping point", "Prepare the next activity"],
      during: ["Use visible countdown", "Save progress", "End without renegotiating"],
      after: ["Move directly to another activity", "Praise stopping", "Review only when calm"],
      examples: [
        { instead: "Turn it off now—I mean it!", try: "The timer ended. Save your place and choose shower or snack next." },
        { instead: "Fine, ten more minutes.", try: "The agreed stopping point stays the same." }
      ]
    },
    S09: {
      title: "Sibling Conflict",
      commonLoop: ["Children compete or collide", "Parent decides quickly", "One child feels unseen", "Conflict shifts toward the parent", "Rivalry grows"],
      before: ["Clarify protected belongings", "Create turn rules", "Build individual connection"],
      during: ["Stop unsafe behavior", "Hear each child briefly", "Assign repair rather than blame labels"],
      after: ["Restore property or relationship", "Practice direct requests", "Notice cooperative moments"],
      examples: [
        { instead: "You are older, just give it to her.", try: "I will hear both sides. Being older does not automatically mean giving up your turn." },
        { instead: "Say sorry right now.", try: "First calm down. Then each person will take one repair step." }
      ]
    },
    S10: {
      title: "Lying",
      commonLoop: ["Child anticipates reaction", "Child hides or changes facts", "Parent interrogates", "Child protects the story", "Trust decreases"],
      before: ["Keep consequences proportionate", "Make truth safer than concealment", "Avoid trap questions"],
      during: ["State what you know", "Invite the accurate version", "Separate mistake from repair"],
      after: ["Complete repair", "Recognize honesty", "Rebuild verification gradually"],
      examples: [
        { instead: "Did you do this? Don’t lie to me.", try: "I already know part of what happened. I need the truthful version so we can repair it." },
        { instead: "You are a liar.", try: "That answer was not truthful. Your next job is to correct it." }
      ]
    },
    S11: {
      title: "Anxiety / Worry",
      commonLoop: ["Child feels uncertainty", "Child seeks reassurance or avoids", "Adult provides certainty or removes demand", "Short-term relief occurs", "Worry becomes more dependent on avoidance"],
      before: ["Preview what is known", "Choose one coping tool", "Define a small brave step"],
      during: ["Validate feeling", "Limit repeated reassurance", "Support action"],
      after: ["Praise courage", "Review what was tolerated", "Increase challenge gradually"],
      examples: [
        { instead: "I promise nothing bad will happen.", try: "I cannot promise everything, but I can help you handle the next step." },
        { instead: "You can stay home if you are scared.", try: "We will make the first step smaller, and we are still moving toward it." }
      ]
    },
    S12: {
      title: "Arguing",
      commonLoop: ["Adult gives direction", "Child challenges wording or fairness", "Adult tries to win the argument", "Both become more rigid", "Original task disappears"],
      before: ["Use short rules", "Choose which issues deserve discussion", "Plan respectful disagreement language"],
      during: ["Acknowledge once", "state boundary", "pause circular argument"],
      after: ["Discuss substance later", "practice respectful disagreement", "repair tone on both sides"],
      examples: [
        { instead: "Do not talk back to me!", try: "You may disagree without insulting. Try the sentence again." },
        { instead: "I will prove why I am right.", try: "I heard your point. The decision is complete for now." }
      ]
    }
  };

  K.getV3ConstructBlueprint = function getV3ConstructBlueprint(id) {
    return this.v3.constructBlueprints[id] || null;
  };

  K.getV3ScenarioBlueprint = function getV3ScenarioBlueprint(idOrKey) {
    const scenario = this.getV3Scenario ? this.getV3Scenario(idOrKey) : null;
    if (scenario?.id && this.v3.scenarioBlueprints[scenario.id]) {
      return this.v3.scenarioBlueprints[scenario.id];
    }
    if (this.v3.scenarioBlueprints[idOrKey]) return this.v3.scenarioBlueprints[idOrKey];
    const entry = Object.entries(this.v3.scenarios || {}).find(([, item]) => {
      return item.key === idOrKey || item.situationKey === idOrKey;
    });
    return entry ? this.v3.scenarioBlueprints[entry[0]] || null : null;
  };

  K.buildV3SevenDayPlan = function buildV3SevenDayPlan(scenarioId, constructIds = []) {
    const scenario = this.getV3ScenarioBlueprint(scenarioId);
    const primary = this.getV3ConstructBlueprint(constructIds[0]);
    const secondary = this.getV3ConstructBlueprint(constructIds[1]);

    return [
      {
        day: "Days 1–2",
        title: "Observe and prepare",
        actions: [
          "Notice the earliest trigger rather than only the final behavior.",
          scenario?.before?.[0] || "Prepare the environment before the difficult moment.",
          primary?.stressTriggers?.[0] ? `Watch especially for ${primary.stressTriggers[0]}.` : "Write down what seems to raise pressure."
        ]
      },
      {
        day: "Days 3–4",
        title: "Test the main strategy",
        actions: [
          scenario?.during?.[0] || "Use one calm, repeatable response.",
          primary?.tryInstead || "Use fewer words and one clear next step.",
          "Repeat the same strategy twice before deciding whether it helped."
        ]
      },
      {
        day: "Days 5–6",
        title: "Add the secondary support",
        actions: [
          secondary?.motivation?.[0] ? `Add ${secondary.motivation[0]}.` : "Add one small support based on what you observed.",
          scenario?.after?.[0] || "Repair and review after calm returns.",
          "Notice whether conflict intensity or recovery time changes."
        ]
      },
      {
        day: "Day 7",
        title: "Review and refine",
        actions: [
          "Keep what reduced conflict or improved recovery.",
          "Remove any step that created more pressure without helping.",
          "Record one thing to test in the next Parenting Optimize session."
        ]
      }
    ];
  };

})(window);
